import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buildReportNavParams } from '../../utils/reportingFilterTranslator';
import { useNavigate } from 'react-router-dom';
import { GenericView } from '../../components/common/table';
import api from '../../api'; // pastikan import api/axios instance
import { EllipsisVertical, ChevronDown, Funnel } from 'lucide-react';
import ColumnChart from '../../components/common/Chart/ColumnChart'
import FunnelChart from '../../components/common/Chart/FunnelChart'
import DonutChart from '../../components/common/Chart/DonutChart'
import { dashboardApi } from './api/dashboardApi'
import LeadsForm from '../Leads/components/LeadsForm'
import { useLeadsForm } from '../Leads/hooks/useLeadsForm'
import Alert from '../../components/Alert/Alert'
import SearchInput from '../../components/common/table/SearchInput'
import { useAuth } from '../../contexts/AuthProvider'
import Modal from '../../components/common/Modal'
import FilterAwarenessBar from '../../components/common/AdvancedFilter/FilterAwarenessBar';
import AdvancedFilterPanel from '../../components/common/AdvancedFilter';
import { getPeriodDateRange } from '../../components/common/AdvancedFilter/periodUtils';
import { usePersistedFilters } from '../../hooks/usePersistedFilters';

// Inline InfoTooltip component for dashboard cards
const InfoTooltip = ({ text, light = false }) => (
  <span className="dashboard-info-tooltip-wrapper" data-tooltip={text}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke={light ? 'rgba(255,255,255,0.8)' : 'currentColor'}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="dashboard-info-icon"
      style={{ color: light ? 'rgba(255,255,255,0.8)' : '#adb5bd', cursor: 'help', flexShrink: 0 }}
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  </span>
);

/** Canonical ids — match `Proposals/index.jsx` and Proposals Reports (`/crm/proposals` query params). */
const DASHBOARD_PROPOSAL_APPROVAL_STATUS_ID = {
  DRAFT: 1,
  APPROVED: 2,
  NEED_APPROVAL: 3,
  REJECTED: 4,
  REVISION: 5
};

const DASHBOARD_PROPOSAL_PROPOSAL_STATUS_ID = {
  DRAFT: 1,
  SEND_TO_CLIENT: 2,
  ACCEPTED: 3,
  REJECTED: 4,
  FEEDBACK: 5
};

/**
 * Map Proposal Breakdown chart label (`p.status_proposal_desc` from API) to `proposalStatus` id for reports.
 * Handles display names, DB enums (e.g. SEND_TO_CLIENT), and Indonesian labels.
 */
function proposalBreakdownLabelToProposalStatusId(label) {
  if (label == null || label === '') return DASHBOARD_PROPOSAL_PROPOSAL_STATUS_ID.DRAFT;
  const raw = String(label).trim();
  if (/^\d+$/.test(raw)) return parseInt(raw, 10);

  const n = raw.toLowerCase();
  const snake = raw.toUpperCase().replace(/\s+/g, '_');

  if (snake === 'DRAFT' || n === 'draft') return DASHBOARD_PROPOSAL_PROPOSAL_STATUS_ID.DRAFT;
  if (['SEND_TO_CLIENT', 'SENT_TO_CLIENT'].includes(snake) || /send\s*to\s*client/i.test(raw) || n.includes('terkirim')) {
    return DASHBOARD_PROPOSAL_PROPOSAL_STATUS_ID.SEND_TO_CLIENT;
  }
  if (snake === 'ACCEPTED' || /^accepted$/i.test(raw) || n.includes('diterima')) {
    return DASHBOARD_PROPOSAL_PROPOSAL_STATUS_ID.ACCEPTED;
  }
  if (snake === 'REJECTED' || /^rejected$/i.test(raw) || n.includes('ditolak') || n.includes('decline')) {
    return DASHBOARD_PROPOSAL_PROPOSAL_STATUS_ID.REJECTED;
  }
  if (snake === 'FEEDBACK' || /^feedback$/i.test(raw)) {
    return DASHBOARD_PROPOSAL_PROPOSAL_STATUS_ID.FEEDBACK;
  }

  if (n.includes('send') && n.includes('client')) return DASHBOARD_PROPOSAL_PROPOSAL_STATUS_ID.SEND_TO_CLIENT;
  if (n.includes('accept')) return DASHBOARD_PROPOSAL_PROPOSAL_STATUS_ID.ACCEPTED;
  if (n.includes('reject') || n.includes('declin')) return DASHBOARD_PROPOSAL_PROPOSAL_STATUS_ID.REJECTED;
  if (n.includes('feedback')) return DASHBOARD_PROPOSAL_PROPOSAL_STATUS_ID.FEEDBACK;
  if (n.includes('draft')) return DASHBOARD_PROPOSAL_PROPOSAL_STATUS_ID.DRAFT;

  return DASHBOARD_PROPOSAL_PROPOSAL_STATUS_ID.DRAFT;
}

// StatNum: renders filtered/total count with loading skeleton and filter indication
const StatNum = ({ loading, filtered, total, isFiltered, light = false }) => {
  if (loading) {
    return light
      ? <span className="stat-loading-light">&nbsp;&nbsp;&nbsp;&nbsp;</span>
      : <span className="stat-loading">&nbsp;&nbsp;&nbsp;&nbsp;</span>;
  }
  const showFiltered = isFiltered && filtered !== total;
  return (
    <>
      <span className={showFiltered ? 'stat-filtered-count' : ''} style={showFiltered ? { paddingTop: '12px', display: 'inline-block' } : {}}>
        {filtered}
      </span>
      {' / '}
      <span style={{ fontSize: '0.7em', opacity: showFiltered ? 0.65 : 0.85 }}>{total}</span>
    </>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const offcanvasRef = useRef(null);
  // Use auth context to check capabilities and get current user
  const { can, capabilitiesLoading, user, capabilities } = useAuth();
  
  // Check if user has can_approve capability (only check when capabilities are loaded)
  const canApprove = !capabilitiesLoading && can('can_approve', 'proposal');

  // Permission helpers for Dashboard view scope
  const canDashboard = (capabilityKey) => {
    // Dashboard has its own module in SetPermission; do NOT fall back
    return can(capabilityKey, 'dashboard');
  };

  const viewScope = useMemo(() => {
    if (capabilitiesLoading) return 'loading';

    // Use ONLY dashboard module capabilities so roles must be configured explicitly there
    const dashboardCaps = capabilities?.dashboard || {};
    if (dashboardCaps.view_global) return 'global';
    if (dashboardCaps.view_department) return 'department';
    if (dashboardCaps.view_own) return 'own';
    return 'none';
  }, [capabilitiesLoading, capabilities?.dashboard]); // Depend on specific dashboard capabilities
  
  // Persist dashboard filters across navigation (sessionStorage), same pattern as Leads/Reports
  const { loadPersistedState, persistState } = usePersistedFilters('dashboard');

  // Ganti data statis dengan state
  const [approvalRequestsData, setApprovalRequestsData] = useState([]);
  const [totalApprovalRequests, setTotalApprovalRequests] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // Pagination state for approval requests
  const [approvalPagination, setApprovalPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalRows: 0,
    rowsPerPage: 10
  });
  
  // Search state for approval requests
  const [approvalSearchQuery, setApprovalSearchQuery] = useState('');
  
  // Serialize filter value (array or string) to API param: "all" or "id1,id2,..."
  const toFilterParam = useCallback((v) => {
    if (Array.isArray(v)) {
      const clean = v.filter((x) => x != null && x !== '' && x !== 'all');
      return clean.length === 0 ? 'all' : clean.join(',');
    }
    return v == null || v === '' ? 'all' : String(v);
  }, []);

  // Normalize people filter to array so stats/total APIs always get consistent "all" or "id1,id2,..."
  const normalizePeopleForApi = useCallback((people) => {
    if (Array.isArray(people)) return people;
    if (people == null || people === '' || people === 'all') return [];
    return [].concat(people);
  }, []);

  // Normalize sector/source/standard filters to array of numeric IDs so API always receives "all" or "id1,id2,..."
  const normalizeFilterIdsForApi = useCallback((value) => {
    if (value == null || value === '' || value === 'all') return [];
    if (Array.isArray(value)) {
      const ids = value
        .map((x) => (x != null && typeof x === 'object' && 'id' in x ? x.id : x))
        .filter((x) => x != null && x !== '' && x !== 'all');
      return ids.map((x) => String(x));
    }
    return [String(value)];
  }, []);

  // Filter states (people, businessUnits, sector, standard, source are multiselect arrays; branchId is single)
  const [filters, setFilters] = useState(() => {
    const defaults = {
      people: [],
      businessUnits: [],
      sector: [],
      standard: [],
      service: [],
      source: [],
      branchId: '',
      period: 'this_month'
    };
    const saved = loadPersistedState();
    if (!saved?.filters) return defaults;
    const { _customDateRange, ...restFilters } = saved.filters;
    return { ...defaults, ...restFilters };
  });

  const serializedFilters = useMemo(() => ({
    source: toFilterParam(filters.source),
    sector: toFilterParam(filters.sector),
    standard: toFilterParam(filters.standard),
    service: toFilterParam(filters.service),
    businessUnits: toFilterParam(filters.businessUnits),
    people: toFilterParam(filters.people),
    branchId: filters.branchId == null || filters.branchId === '' || filters.branchId === 'all' ? 'all' : String(filters.branchId)
  }), [filters.source, filters.sector, filters.standard, filters.service, filters.businessUnits, filters.people, filters.branchId, toFilterParam]);

  // Custom date range state — MUST be declared before the persist-filters effect below,
  // which reads it in its dependency array. Reading a `const` before its useState line
  // throws "Cannot access 'customDateRange' before initialization" (TDZ) on every render.
  const [customDateRange, setCustomDateRange] = useState(() => {
    const saved = loadPersistedState();
    return saved?.filters?._customDateRange || { startDate: '', endDate: '' };
  });

  // Sources data state
  const [sourcesData, setSourcesData] = useState([]);

  // Sectors data state
  const [sectorsData, setSectorsData] = useState([]);

  // Standards data state
  const [standardsData, setStandardsData] = useState([]);

  // Services data state
  const [servicesData, setServicesData] = useState([]);
  
  // Business Units data state
  const [businessUnitsData, setBusinessUnitsData] = useState([]);
  
  // Sales Users data state
  const [salesUsersData, setSalesUsersData] = useState([]);

  // HRIS user status lookup: userId → status ('0' = inactive)
  const [dashboardUserStatusMap, setDashboardUserStatusMap] = useState(new Map());
  useEffect(() => {
    api.get('/md/internal-users/option-internal-user')
      .then(res => {
        const map = new Map();
        (res.data || []).forEach(u => { if (u.id != null) map.set(String(u.id), u.status); });
        setDashboardUserStatusMap(map);
      })
      .catch(() => {});
  }, []);

  // Branches data state (current user's branches for filter)
  const [branches, setBranches] = useState([]);

  // Enforce permission-based filter locking (own / department / global)
  useEffect(() => {
    if (capabilitiesLoading) return;
    if (!user) return;

    // If user has no view capability, keep defaults (backend might still enforce)
    if (viewScope === 'none' || viewScope === 'loading') return;

    const myUserId = user?.id != null ? String(user.id) : null;
    let myDeptId =
      user?.departmentId != null ? String(user.departmentId)
        : user?.department_id != null ? String(user.department_id)
          : user?.department?.id != null ? String(user.department.id)
            : null;

    // If department is stored as a name string, find the ID from businessUnitsData
    if (!myDeptId && user?.department && typeof user.department === 'string') {
      const deptObj = (businessUnitsData || []).find(d =>
        String(d?.name).toLowerCase() === String(user.department).toLowerCase()
      );
      if (deptObj?.id) {
        myDeptId = String(deptObj.id);
      }
    }


    // Use functional update to avoid dependency on current filters state and prevent unnecessary re-renders
    setFilters(prev => {
      let next = { ...prev };
      let hasChanges = false;

      if (viewScope === 'own') {
        const peopleArr = Array.isArray(next.people) ? next.people : (next.people && next.people !== 'all' ? [next.people] : []);
        if (myUserId && (peopleArr.length !== 1 || String(peopleArr[0]) !== String(myUserId))) {
          next.people = [myUserId];
          hasChanges = true;
        }
        const buArr = Array.isArray(next.businessUnits) ? next.businessUnits : (next.businessUnits && next.businessUnits !== 'all' ? [next.businessUnits] : []);
        if (myDeptId && (buArr.length !== 1 || String(buArr[0]) !== String(myDeptId))) {
          next.businessUnits = [myDeptId];
          hasChanges = true;
        }
      }

      if (viewScope === 'department') {
        const buArr = Array.isArray(next.businessUnits) ? next.businessUnits : (next.businessUnits && next.businessUnits !== 'all' ? [next.businessUnits] : []);
        if (myDeptId && (buArr.length !== 1 || String(buArr[0]) !== String(myDeptId))) {
          next.businessUnits = [myDeptId];
          hasChanges = true;
        }

        // If current selected person is outside the department, reset to all
        const peopleArr = Array.isArray(next.people) ? next.people : (next.people && next.people !== 'all' ? [next.people] : []);
        const selectedId = peopleArr.length === 1 ? peopleArr[0] : null;
        if (selectedId && selectedId !== 'all' && myDeptId) {
          const selectedUser = (salesUsersData || []).find(u => String(u?.id) === String(selectedId));
          const selectedDeptId = selectedUser?.departmentId ?? selectedUser?.department_id ?? selectedUser?.department?.id;
          if (selectedDeptId != null && String(selectedDeptId) !== String(myDeptId)) {
            next.people = [];
            hasChanges = true;
          }
        }
      }

      // Only return new object if there are actual changes to prevent unnecessary re-renders
      return hasChanges ? next : prev;
    });
  }, [capabilitiesLoading, user, viewScope, salesUsersData, businessUnitsData]);

  const isPeopleLocked = viewScope === 'own';
  const isBusinessUnitsLocked = viewScope === 'department' || viewScope === 'own';

  // Memoize locked states to prevent unnecessary recalculations
  const lockedStates = useMemo(() => ({
    people: isPeopleLocked,
    businessUnits: isBusinessUnitsLocked
  }), [isPeopleLocked, isBusinessUnitsLocked]);

  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [dateRangeError, setDateRangeError] = useState('');

  // Persist filters + custom date range so they survive navigation (e.g. back from a lead/proposal detail)
  useEffect(() => {
    persistState({ filters: { ...filters, _customDateRange: customDateRange }, search: '', page: 0, size: 0 });
  }, [filters, customDateRange, persistState]);

  // Compute active filter tags for display (exclude locked filters and default period)
  const activeFilterTags = useMemo(() => {
    const tags = [];

    // People filter (only if not locked)
    if (!lockedStates.people && Array.isArray(filters.people) && filters.people.length > 0) {
      const names = filters.people.map(id => {
        const user = salesUsersData.find(u => String(u.id) === String(id));
        return user ? (user.name || user.username || String(id)) : String(id);
      });
      tags.push({ key: 'people', label: names.length === 1 ? names[0] : `${names.length} People`, color: 'blue', icon: '👤' });
    }

    // Business Units filter (only if not locked)
    if (!lockedStates.businessUnits && Array.isArray(filters.businessUnits) && filters.businessUnits.length > 0) {
      const names = filters.businessUnits.map(id => {
        const dept = businessUnitsData.find(d => String(d.id) === String(id));
        return dept ? dept.name : String(id);
      });
      tags.push({ key: 'businessUnits', label: names.length === 1 ? names[0] : `${names.length} Departments`, color: 'purple', icon: '🏢' });
    }

    // Sector filter
    if (Array.isArray(filters.sector) && filters.sector.length > 0) {
      const names = filters.sector.map(id => {
        const s = sectorsData.find(x => String(x.id) === String(id));
        return s ? s.name : String(id);
      });
      tags.push({ key: 'sector', label: names.length === 1 ? names[0] : `${names.length} Sectors`, color: 'teal', icon: '🏭' });
    }

    // Standard filter
    if (Array.isArray(filters.standard) && filters.standard.length > 0) {
      const names = filters.standard.map(id => {
        const s = standardsData.find(x => String(x.id) === String(id));
        return s ? s.name : String(id);
      });
      tags.push({ key: 'standard', label: names.length === 1 ? names[0] : `${names.length} Standards`, color: 'orange', icon: '📋' });
    }

    // Service filter
    if (Array.isArray(filters.service) && filters.service.length > 0) {
      const names = filters.service.map(id => {
        const s = servicesData.find(x => String(x.id) === String(id));
        return s ? s.name : String(id);
      });
      tags.push({ key: 'service', label: names.length === 1 ? names[0] : `${names.length} Services`, color: 'indigo', icon: '🛠️' });
    }

    // Source filter
    if (Array.isArray(filters.source) && filters.source.length > 0) {
      const names = filters.source.map(id => {
        const s = sourcesData.find(x => String(x.id) === String(id));
        return s ? s.name : String(id);
      });
      tags.push({ key: 'source', label: names.length === 1 ? names[0] : `${names.length} Sources`, color: 'cyan', icon: '📡' });
    }

    // Branch filter
    if (filters.branchId && filters.branchId !== '' && filters.branchId !== 'all') {
      const branch = branches.find(b => String(b.id) === String(filters.branchId));
      tags.push({ key: 'branchId', label: branch ? branch.name : `Branch ${filters.branchId}`, color: 'green', icon: '📍' });
    }

    // Period filter (only show if not default)
    const periodLabels = {
      'last_3_days': 'Last 3 Days', 'last_7_days': 'Last 7 Days',
      'this_month': 'This Month', 'last_month': 'Last Month',
      'this_year': 'This Year', 'last_year': 'Last Year', 'custom': 'Custom Range'
    };
    if (filters.period && filters.period !== 'this_month') {
      const label = filters.period === 'custom' && customDateRange.startDate && customDateRange.endDate
        ? `${customDateRange.startDate} → ${customDateRange.endDate}`
        : (periodLabels[filters.period] || filters.period);
      tags.push({ key: 'period', label, color: 'red', icon: '📅' });
    }

    return tags;
  }, [filters, lockedStates, salesUsersData, businessUnitsData, sectorsData, standardsData, servicesData, sourcesData, branches, customDateRange]);

  const hasActiveFilters = activeFilterTags.length > 0;

  const resolvedBusinessUnitsOptions = useMemo(() => {
    if (!lockedStates.businessUnits) {
      return businessUnitsData || [];
    }

    // For locked state, we only need user's department info, not the full user object
    let myDeptId = null;
    let deptName = 'Department';

    if (user) {
      myDeptId =
        user?.departmentId != null ? String(user.departmentId)
          : user?.department_id != null ? String(user.department_id)
            : user?.department?.id != null ? String(user.department.id)
              : null;

      // If department is stored as a name string, find the ID from businessUnitsData
      if (!myDeptId && user?.department && typeof user.department === 'string') {
        const deptObj = (businessUnitsData || []).find(d =>
          String(d?.name).toLowerCase() === String(user.department).toLowerCase()
        );
        if (deptObj?.id) {
          myDeptId = String(deptObj.id);
          deptName = deptObj.name;
        }
      } else if (myDeptId) {
        const deptObj = (businessUnitsData || []).find(d => String(d?.id) === String(myDeptId));
        deptName = deptObj?.name ||
          user?.departmentName ||
          user?.department?.name ||
          user?.department ||
          `Department ${myDeptId}`;
      }
    }

    if (!myDeptId) {
      return businessUnitsData || [];
    }

    return [{ id: myDeptId, name: deptName }];
  }, [lockedStates.businessUnits, businessUnitsData, user?.departmentId, user?.department_id, user?.department, user?.departmentName]);

  // Apply "(Inactive)" label for users with status '0'
  const withInactiveName = (u) => {
    const status = dashboardUserStatusMap.get(String(u?.id));
    if (status === '0') return { ...u, name: `${u.name} (Inactive)` };
    return u;
  };

  const resolvedPeopleOptions = useMemo(() => {
    // Empty selection means "all"; no redundant "All People" option in the list
    if (viewScope === 'own') {
      const myUserId = user?.id != null ? String(user.id) : null;
      const myName =
        user?.name ||
        `${user?.firstName || ''} ${user?.lastName || ''}`.trim() ||
        user?.username ||
        'User';
      return myUserId ? [{ id: myUserId, name: myName }] : [];
    }

    if (viewScope === 'department') {
      let myDeptId =
        user?.departmentId != null ? String(user.departmentId)
          : user?.department_id != null ? String(user.department_id)
            : user?.department?.id != null ? String(user.department.id)
              : null;

      if (!myDeptId && user?.department && typeof user.department === 'string') {
        const deptObj = (businessUnitsData || []).find(d =>
          String(d?.name).toLowerCase() === String(user.department).toLowerCase()
        );
        if (deptObj?.id) {
          myDeptId = String(deptObj.id);
        }
      }

      const filtered = (salesUsersData || []).filter(u => {
        if (!myDeptId) return true;
        const deptId = u?.departmentId ?? u?.department_id ?? u?.department?.id;
        return deptId != null && String(deptId) === String(myDeptId);
      });

      return filtered.map(withInactiveName);
    }

    // Global scope: cascade by selected department filter
    const selectedDepts = (Array.isArray(filters.businessUnits) ? filters.businessUnits : [])
      .filter(d => d != null && d !== 'all')
      .map(String);
    const base = (salesUsersData || []).map(withInactiveName);
    if (!selectedDepts.length) return base;
    return base.filter(u => {
      const deptId = u?.departmentId ?? u?.department_id ?? u?.department?.id;
      return deptId != null && selectedDepts.includes(String(deptId));
    });
  }, [viewScope, lockedStates.people, filters.businessUnits, salesUsersData, businessUnitsData, dashboardUserStatusMap, user?.id, user?.name, user?.firstName, user?.lastName, user?.username, user?.departmentId, user?.department_id, user?.department]);
  
  // Dashboard data states
  const [dashboardData, setDashboardData] = useState({
    statistics: {
      totalProspect: 0,
      newProspect: 0,
      convertedToProposal: 0,
      totalLeads: 0,
      totalProjects: 0,
      latestFollowUpLeads: 0,
      latestUpdatedLeads: 0,
      latestUpdatedProposal: 0,
      proposalNotConverted: 0,
      convertedToProject: 0,
      negotiation: 0,
      closeWon1: 0,
      closeWon2: 0,
      closeWon3: 0,
      won: 0,
      lost: 0,
      waitingForApproval: 0,
      approvalApproved: 0,
      sendToClient: 0,
      needRevision: 0,
      decline: 0,
      accepted: 0
    },
    teamMemberData: { data: [], categories: [], userIds: [] },
    salesFunnelData: { data: [], categories: [], proposalValue: 0, formattedProposalValue: '', negotiationValue: 0, contractWonValue: 0, projectsValue: 0 },
    proposalBreakdownData: { series: [], labels: [], colors: [] },
    leadsBySourceData: { series: [], labels: [] },
    leadsByIndustryData: { data: [], categories: [] },
    taskManagementData: []
  });

  // State for To Do and In Progress tasks
  const [priorityTasks, setPriorityTasks] = useState([]);
  const [allPriorityTasks, setAllPriorityTasks] = useState([]); // Store all tasks for pagination
  const [loadingPriorityTasks, setLoadingPriorityTasks] = useState(false);
  
  // Pagination state for priority tasks
  const [priorityTasksPagination, setPriorityTasksPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalRows: 0,
    rowsPerPage: 5
  });
  
  // Store all approval requests for client-side pagination when server doesn't paginate
  const [allApprovalRequests, setAllApprovalRequests] = useState([]);
  
  const [dashboardLoading, setDashboardLoading] = useState(false);
  
  // Total counts without filters
  const [totalCounts, setTotalCounts] = useState({
    totalProspect: 0,
    newProspect: 0,
    convertedToProposal: 0,
    totalLeads: 0,
    totalProjects: 0,
    latestFollowUpLeads: 0,
    latestUpdatedLeads: 0,
    latestUpdatedProposal: 0,
    proposalNotConverted: 0,
    convertedToProject: 0,
    negotiation: 0,
    closeWon1: 0,
    closeWon2: 0,
    closeWon3: 0,
    won: 0,
    lost: 0,
    waitingForApproval: 0,
    approvalApproved: 0,
    sendToClient: 0,
    needRevision: 0,
    decline: 0,
    accepted: 0
  });
  
  // Leads form state
  const [isLeadsModalOpen, setIsLeadsModalOpen] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });
  
  // ✅ TAMBAHAN: Approval detail modal state
  const [showApprovalDetailModal, setShowApprovalDetailModal] = useState(false);
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [approvalWorkflowItems, setApprovalWorkflowItems] = useState([]);
  const [approvalNote, setApprovalNote] = useState('');
  const [loadingApprovalDetails, setLoadingApprovalDetails] = useState(false);
  
  // Use leads form hook
  const {
    formData,
    formErrors,
    serviceSections,
    uploadedFiles,
    handleInputChange,
    handleTextChange,
    handleFormSubmit,
    addServiceSection,
    deleteServiceSection,
    onDrop,
    removeFile,
    resetForm,
    options,
    fetchContactOptions,
    standardsOptionsMap
  } = useLeadsForm();

  // Fetch sources data from backend
  const fetchSourcesData = async () => {
    try {
      const response = await api.get('/dashboard/sources');
      if (response.data && response.data.sources) {
        setSourcesData(response.data.sources);
      }
    } catch (error) {
      console.error('Error fetching sources data:', error);
      // Set empty array as fallback
      setSourcesData([]);
    }
  };

  // Fetch sectors data from backend
  const fetchSectorsData = async () => {
    try {
      const response = await api.get('/dashboard/sectors');
      if (response.data && response.data.sectors) {
        setSectorsData(response.data.sectors);
      }
    } catch (error) {
      console.error('Error fetching sectors data:', error);
      // Set empty array as fallback
      setSectorsData([]);
    }
  };

  // Fetch standards data from backend
  const fetchStandardsData = async () => {
    try {
      const response = await api.get('/dashboard/standards');
      if (response.data && response.data.standards) {
        setStandardsData(response.data.standards);
      } else {
        setStandardsData([]);
      }
    } catch (error) {
      console.error('Error fetching standards data:', error);
      // Set empty array as fallback
      setStandardsData([]);
    }
  };

  // Fetch services data from backend
  const fetchServicesData = async () => {
    try {
      const response = await api.get('/dashboard/services');
      if (response.data && response.data.services) {
        setServicesData(response.data.services);
      } else {
        setServicesData([]);
      }
    } catch (error) {
      console.error('Error fetching services data:', error);
      // Set empty array as fallback
      setServicesData([]);
    }
  };

  // Fetch business units data from backend
  const fetchBusinessUnitsData = async () => {
    try {
      const response = await api.get('/dashboard/business-units');
      if (response.data && response.data.businessUnits) {
        setBusinessUnitsData(response.data.businessUnits);
      }
    } catch (error) {
      console.error('Error fetching business units data:', error);
      // Set empty array as fallback
      setBusinessUnitsData([]);
    }
  };

  // Fetch sales users data from backend
  const fetchSalesUsersData = async () => {
    try {
      const response = await api.get('/dashboard/sales-users');
      if (response.data && response.data.salesUsers) {
        setSalesUsersData(response.data.salesUsers);
      }
    } catch (error) {
      console.error('Error fetching sales users data:', error);
      // Set empty array as fallback
      setSalesUsersData([]);
    }
  };

  // Fetch current user's branches for filter
  const fetchBranchesData = async () => {
    if (!user?.id) return;
    try {
      const res = await api.get(`/user-branches/user/${user.id}`);
      const branchData = res.data?.data ?? res.data ?? [];
      setBranches(Array.isArray(branchData) ? branchData : []);
    } catch (err) {
      console.error('Error fetching user branches for dashboard filter:', err);
      setBranches([]);
    }
  };

  // Fetch total counts (all-time, same dimension filters as filtered data)
  // Uses /statistics/total which applies no date filter but same source/sector/standard/department/people filters
  const fetchTotalCounts = async (currentFilters = {}) => {
    try {
      const sourceP = toFilterParam(normalizeFilterIdsForApi(currentFilters.source));
      const sectorP = toFilterParam(normalizeFilterIdsForApi(currentFilters.sector));
      const standardP = toFilterParam(normalizeFilterIdsForApi(currentFilters.standard));
      const serviceP = toFilterParam(normalizeFilterIdsForApi(currentFilters.service));
      const businessUnitsP = toFilterParam(currentFilters.businessUnits);
      const peopleP = toFilterParam(normalizePeopleForApi(currentFilters.people));
      const branchIdP = currentFilters.branchId == null || currentFilters.branchId === '' || currentFilters.branchId === 'all' ? 'all' : String(currentFilters.branchId);
      const statisticsUrl = `/dashboard/statistics/total?source=${encodeURIComponent(sourceP)}&sector=${encodeURIComponent(sectorP)}&standard=${encodeURIComponent(standardP)}&service=${encodeURIComponent(serviceP)}&businessUnit=${encodeURIComponent(businessUnitsP)}&userId=${encodeURIComponent(peopleP)}&branchId=${encodeURIComponent(branchIdP)}`;

      const statisticsResponse = await api.get(statisticsUrl);
      const data = statisticsResponse?.data;
      if (data && typeof data === 'object') {
        setTotalCounts(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.error('Error fetching total counts:', error);
      // Fallback: use filtered statistics as total so we don't show "X / 0" when total endpoint fails
      setTotalCounts(prev => {
        const fromFiltered = dashboardData?.statistics;
        if (fromFiltered && typeof fromFiltered === 'object') {
          return { ...prev, ...fromFiltered };
        }
        return prev;
      });
    }
  };

  // Fetch all tasks (to do, in progress, done) sorted by priority
  const fetchPriorityTasks = async (page = 1, size = 5, currentFilters = filters) => {
    setLoadingPriorityTasks(true);
    try {
      // Keep the priority list consistent with the dashboard period filter.
      const taskParams = new URLSearchParams();
      const hasCompleteCustomRange = customDateRange.startDate && customDateRange.endDate;
      const period = currentFilters?.period || (hasCompleteCustomRange ? 'custom' : 'this_month');
      if (period !== 'all') {
        if (period === 'custom' && customDateRange.startDate && customDateRange.endDate) {
          taskParams.append('dueDateFrom', customDateRange.startDate);
          taskParams.append('dueDateTo', customDateRange.endDate);
        } else {
          const range = getPeriodDateRange(period);
          if (range.startDate) taskParams.append('dueDateFrom', range.startDate);
          if (range.endDate) taskParams.append('dueDateTo', range.endDate);
        }
      }
      const taskQuery = taskParams.toString();
      const response = await api.get(taskQuery ? `/tasks?${taskQuery}` : '/tasks');
      let tasks = Array.isArray(response.data) ? response.data : [];

      // Filter tasks by assigned to current user only (no status filter)
      const filteredTasks = tasks.filter(task => {
        // Filter by assigned user - check if task is assigned to current user
        if (!user || !user.id) {
          return false; // No user logged in, don't show any tasks
        }

        const isAssignedToUser = task.assignedUserId === user.id ||
                                 task.assignedUserId === parseInt(user.id, 10) ||
                                 String(task.assignedUserId) === String(user.id);

        return isAssignedToUser;
      });
      
      // Sort by priority (descending - highest priority first)
      // Priority order: High > Medium > Low
      const priorityOrder = { 'high': 3, 'medium': 2, 'low': 1 };
      const sortedTasks = filteredTasks.sort((a, b) => {
        const priorityA = priorityOrder[a.priority?.toLowerCase()] || 0;
        const priorityB = priorityOrder[b.priority?.toLowerCase()] || 0;
        
        // If priorities are equal, sort by due date (earliest first)
        if (priorityA === priorityB) {
          const dueDateA = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
          const dueDateB = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
          return dueDateA - dueDateB;
        }
        
        return priorityB - priorityA; // Descending order (highest priority first)
      });
      
      // Store all tasks
      setAllPriorityTasks(sortedTasks);
      
      // Apply pagination
      const startIndex = (page - 1) * size;
      const endIndex = startIndex + size;
      const paginatedTasks = sortedTasks.slice(startIndex, endIndex);
      
      setPriorityTasks(paginatedTasks);
      setPriorityTasksPagination({
        currentPage: page,
        totalPages: Math.ceil(sortedTasks.length / size),
        totalRows: sortedTasks.length,
        rowsPerPage: size
      });
    } catch (error) {
      console.error('Error fetching priority tasks:', error);
      setPriorityTasks([]);
      setAllPriorityTasks([]);
      setPriorityTasksPagination({
        currentPage: 1,
        totalPages: 0,
        totalRows: 0,
        rowsPerPage: 5
      });
    } finally {
      setLoadingPriorityTasks(false);
    }
  };

  // Pagination handlers for priority tasks
  const handlePriorityTasksPageChange = (page) => {
    const rowsPerPage = priorityTasksPagination.rowsPerPage;
    const startIndex = (page - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const paginatedTasks = allPriorityTasks.slice(startIndex, endIndex);
    
    setPriorityTasks(paginatedTasks);
    setPriorityTasksPagination(prev => ({ ...prev, currentPage: page }));
  };

  const handlePriorityTasksRowsPerPageChange = (size) => {
    const startIndex = 0;
    const endIndex = size;
    const paginatedTasks = allPriorityTasks.slice(startIndex, endIndex);
    
    setPriorityTasks(paginatedTasks);
    setPriorityTasksPagination({
      currentPage: 1,
      totalPages: Math.ceil(allPriorityTasks.length / size),
      totalRows: allPriorityTasks.length,
      rowsPerPage: size
    });
  };

  // Fetch dashboard data from new API
  const fetchDashboardData = async (currentFilters) => {
    setDashboardLoading(true);
    try {
      // An empty preset with both manual dates is a custom range. This is the
      // state produced by the shared filter when users enter dates directly.
      const hasCompleteCustomRange = customDateRange.startDate && customDateRange.endDate;
      const period = currentFilters.period || (hasCompleteCustomRange ? 'custom' : 'this_month');
      const source = toFilterParam(normalizeFilterIdsForApi(currentFilters.source));
      const sector = toFilterParam(normalizeFilterIdsForApi(currentFilters.sector));
      const standard = toFilterParam(normalizeFilterIdsForApi(currentFilters.standard));
      const service = toFilterParam(normalizeFilterIdsForApi(currentFilters.service));
      const businessUnits = toFilterParam(currentFilters.businessUnits);
      const people = toFilterParam(normalizePeopleForApi(currentFilters.people));
      const branchId = currentFilters.branchId == null || currentFilters.branchId === '' || currentFilters.branchId === 'all' ? 'all' : String(currentFilters.branchId);

      // ✅ PERBAIKAN: Handle custom date range untuk statistics API
      // ✅ FIX: URL encode all parameters to ensure special characters are handled correctly
      let statisticsUrl = `/dashboard/statistics?period=${encodeURIComponent(period)}&source=${encodeURIComponent(source)}&sector=${encodeURIComponent(sector)}&standard=${encodeURIComponent(standard)}&service=${encodeURIComponent(service)}&businessUnit=${encodeURIComponent(businessUnits)}&userId=${encodeURIComponent(people)}&branchId=${encodeURIComponent(branchId)}`;
      
      // ✅ FIX: For "last_year", send as custom date range to ensure correct filtering
      // Backend's last_year filter uses >= which includes current year, so we use custom dates instead
      // This ensures we only get data from 2024 (Jan 1, 2024 to Dec 31, 2024)
      if (period === 'last_year') {
        const today = new Date();
        const lastYearStart = new Date(today.getFullYear() - 1, 0, 1); // January 1 of last year
        const lastYearEnd = new Date(today.getFullYear() - 1, 11, 31); // December 31 of last year
        
        // Format dates as YYYY-MM-DD
        const formatDate = (date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };
        
        statisticsUrl = `/dashboard/statistics?period=custom_range&source=${encodeURIComponent(source)}&sector=${encodeURIComponent(sector)}&standard=${encodeURIComponent(standard)}&service=${encodeURIComponent(service)}&businessUnit=${encodeURIComponent(businessUnits)}&userId=${encodeURIComponent(people)}&branchId=${encodeURIComponent(branchId)}`;
        statisticsUrl += `&customStartDate=${formatDate(lastYearStart)}&customEndDate=${formatDate(lastYearEnd)}`;
      } else if (period === 'custom' && customDateRange.startDate && customDateRange.endDate) {
        // Jika period adalah 'custom', tambahkan custom date parameters
        statisticsUrl += `&customStartDate=${customDateRange.startDate}&customEndDate=${customDateRange.endDate}`;
      }
      
      // Fetch statistics from new backend API
      const statisticsResponse = await api.get(statisticsUrl);
      if (statisticsResponse.data) {
        setDashboardData(prev => ({
          ...prev,
          statistics: statisticsResponse.data
        }));
      }
      
      // Keep existing dashboard API for other data (charts, etc.)
      // ✅ Pass normalized sector/source/standard IDs so dashboard API and statistics use same filter values
      const normalizedSector = normalizeFilterIdsForApi(currentFilters.sector);
      const normalizedSource = normalizeFilterIdsForApi(currentFilters.source);
      const normalizedStandard = normalizeFilterIdsForApi(currentFilters.standard);
      const normalizedService = normalizeFilterIdsForApi(currentFilters.service);
      let filtersWithCustomDate = {
        ...currentFilters,
        sector: normalizedSector.length ? normalizedSector : currentFilters.sector,
        source: normalizedSource.length ? normalizedSource : currentFilters.source,
        standard: normalizedStandard.length ? normalizedStandard : currentFilters.standard,
        service: normalizedService.length ? normalizedService : currentFilters.service,
        customStartDate: period === 'custom' ? customDateRange.startDate : null,
        customEndDate: period === 'custom' ? customDateRange.endDate : null
      };
      // ✅ FIX: For "last_year", convert to custom date range for dashboard API
      if (currentFilters.period === 'last_year') {
        const today = new Date();
        const lastYearStart = new Date(today.getFullYear() - 1, 0, 1); // January 1 of last year
        const lastYearEnd = new Date(today.getFullYear() - 1, 11, 31); // December 31 of last year
        const formatDate = (date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };
        filtersWithCustomDate = {
          ...filtersWithCustomDate,
          period: 'custom_range',
          customStartDate: formatDate(lastYearStart),
          customEndDate: formatDate(lastYearEnd)
        };
      }
      const response = await dashboardApi.getAllDashboardData(filtersWithCustomDate);
      if (response && response.success) {
        setDashboardData(prev => {
          const incoming = response.data || {};
          const merged = { ...prev, ...incoming };
          // Guard: a null/omitted field from the API must never overwrite a renderable
          // default, otherwise the chart/table render (.map / property access) throws.
          return {
            ...merged,
            teamMemberData: merged.teamMemberData || prev.teamMemberData,
            // Deep-merge to avoid losing value fields when API only returns {data, categories}
            salesFunnelData: {
              ...(prev.salesFunnelData || {}),
              ...(incoming.salesFunnelData || {}),
            },
            proposalBreakdownData: merged.proposalBreakdownData || prev.proposalBreakdownData,
            leadsBySourceData: merged.leadsBySourceData || prev.leadsBySourceData,
            leadsByIndustryData: merged.leadsByIndustryData || prev.leadsByIndustryData,
            taskManagementData: Array.isArray(merged.taskManagementData) ? merged.taskManagementData : [],
            statistics: statisticsResponse.data || prev.statistics, // Override statistics with new API data
          };
        });
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Handle error silently
    } finally {
      setDashboardLoading(false);
    }
  };

  // Helper function to sort by due date (oldest first)
  const sortByDueDate = (data) => {
    return [...data].sort((a, b) => {
      const dueDateA = a.duedate || a.dueDate || a.due_date;
      const dueDateB = b.duedate || b.dueDate || b.due_date;
      
      if (!dueDateA && !dueDateB) return 0;
      if (!dueDateA) return 1; // Put items without due date at the end
      if (!dueDateB) return -1;
      
      try {
        const dateA = new Date(dueDateA);
        const dateB = new Date(dueDateB);
        return dateA - dateB; // ASC: oldest first
      } catch (error) {
        return 0;
      }
    });
  };

  // Fetch approval requests with pagination and search
  const fetchApprovalRequests = async (page = 1, size = 10, searchQuery = '') => {
    setLoading(true);
    try {
      const params = {
        page: page - 1, // Backend expects 0-based page
        size: size,
        sortBy: 'id',
        sortDir: 'desc'
      };
      
      // Add search parameter if provided
      if (searchQuery && searchQuery.trim()) {
        params.search = searchQuery.trim();
      }
      
      const res = await api.get('/crm/proposals/need-approval', { params });

      // Handle both paginated and non-paginated responses
      if (res.data.content !== undefined) {
        // Paginated response from server
        const responseData = res.data.content || [];
        // ✅ Sort by due date (oldest first) as fallback
        const sortedData = sortByDueDate(responseData);
        setApprovalRequestsData(sortedData);
        
        setApprovalPagination({
          currentPage: (res.data.currentPage || 0) + 1, // Convert to 1-based
          totalPages: res.data.totalPages || 0,
          totalRows: res.data.totalElements || 0,
          rowsPerPage: res.data.size || size
        });
      } else {
        // Non-paginated response - apply client-side pagination
        const dataArray = Array.isArray(res.data) ? res.data : [];
        
        // ✅ Sort by due date (oldest first)
        const sortedArray = sortByDueDate(dataArray);
        
        // Store all data for client-side pagination (only on first load or when no search)
        if (!searchQuery || !searchQuery.trim()) {
          setAllApprovalRequests(sortedArray);
        }
        
        // If we have a search query and the server returned all data, use client-side search
        let dataToPaginate = sortedArray;
        if (searchQuery && searchQuery.trim() && allApprovalRequests.length > 0) {
          const searchResults = performClientSideSearch(searchQuery);
          dataToPaginate = sortByDueDate(searchResults);
        }
        
        // Apply client-side pagination
        const startIndex = (page - 1) * size;
        const endIndex = startIndex + size;
        const paginatedData = dataToPaginate.slice(startIndex, endIndex);
        
        setApprovalRequestsData(paginatedData);
        setApprovalPagination({
          currentPage: page,
          totalPages: Math.ceil(dataToPaginate.length / size),
          totalRows: dataToPaginate.length,
          rowsPerPage: size
        });
      }
    } catch (err) {
      console.error('Error fetching approval requests:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      
      setApprovalRequestsData([]);
      setApprovalPagination({
        currentPage: 1,
        totalPages: 0,
        totalRows: 0,
        rowsPerPage: 10
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch total count of approval requests for the waiting request display
  const fetchTotalApprovalRequests = async () => {
    try {
      const res = await api.get('/crm/proposals/need-approval', { 
        params: { page: 0, size: 1, sortBy: 'id', sortDir: 'desc' } 
      });
      
      // Get total count from response
      if (res.data.totalElements !== undefined) {
        setTotalApprovalRequests(res.data.totalElements);
      } else if (Array.isArray(res.data)) {
        setTotalApprovalRequests(res.data.length);
      } else {
        setTotalApprovalRequests(0);
      }
    } catch (err) {
      console.error('Error fetching total approval requests:', err);
      setTotalApprovalRequests(0);
    }
  };

  // Fetch data dari API - only run once when component mounts and capabilities are loaded
  useEffect(() => {
    // Wait for capabilities to load before checking permissions
    if (capabilitiesLoading) {
      return;
    }

    // Only fetch approval requests if user has can_approve capability
    if (canApprove) {
      fetchApprovalRequests(1, 10, ''); // Start with 10 rows per page
      fetchTotalApprovalRequests(); // Fetch total count for waiting request display
    }

    // Fetch initial data - dashboard data will be fetched by the filters useEffect
    fetchTotalCounts(filters); // Fetch total counts with current filters
    fetchSourcesData();
    fetchSectorsData();
    fetchStandardsData();
    fetchServicesData();
    fetchBusinessUnitsData();
    fetchSalesUsersData();
    if (user && user.id) {
      fetchPriorityTasks(1, 5); // Fetch To Do and In Progress tasks with pagination
      fetchBranchesData();
    }
  }, [capabilitiesLoading]); // Only depend on capabilitiesLoading to avoid frequent re-runs

  useEffect(() => {
    if (user?.id) fetchBranchesData();
  }, [user?.id]);

  // Separate useEffect for approval requests that depends on canApprove
  useEffect(() => {
    if (capabilitiesLoading || !canApprove) return;

    fetchApprovalRequests(1, 10, ''); // Start with 10 rows per page
    fetchTotalApprovalRequests(); // Fetch total count for waiting request display
  }, [canApprove, capabilitiesLoading]);

  // Fetch dashboard data when filters change - debounced to avoid excessive API calls
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchDashboardData(filters);
      fetchPriorityTasks(1, 5, filters);
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [filters, customDateRange]);

  // Fetch total counts when filters change - debounced to avoid excessive API calls
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchTotalCounts(filters);
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [filters, customDateRange]);


  // Table columns configuration
  const columns = [
    {
      key: 'proposalcode',
      label: 'Proposal Number',
      sortable: true,
      render: (value, row) => {
        if (!value) return '-';
        return (
          <a
            href="#"
            className="text-primary text-decoration-none"
            onClick={(e) => {

              e.preventDefault();
              if (row.leadid) {
                // navigate(`/proposals/detail/${row.leadid}`);
                navigate(`/proposals/${row.leadid}/preview`);
              }
            }}
            onContextMenu={(e) => {
              e.preventDefault();
              if (row.leadid) {
                // Open in new tab on right-click
                window.open(`/proposals/${row.leadid}/preview`, '_blank');
              }
            }}
            style={{ cursor: 'pointer' }}
          >
            {value}
          </a>
        );
      }
    },
    { key: 'client', label: 'Client', sortable: true },
    { key: 'initiator', label: 'Initiator', sortable: true },
    {
      key: 'request_date',
      label: 'Request Date',
      sortable: true,
      type: 'date',
      render: (value, row) => {
        // Support both request_date and requestDate field names
        const requestDate = value || row.requestDate || row.request_date;
        if (!requestDate) return '-';
        const date = new Date(requestDate);
        return date.toLocaleDateString();
      }
    },
    {
      key: 'duedate',
      label: 'Due Date',
      sortable: true,
      type: 'date',
      render: (value, row) => {
        const dueDate = value || row.dueDate || row.due_date || row.duedate;
        if (!dueDate) return '-';
        const date = new Date(dueDate);
        return date.toLocaleDateString();
      }
    },
    {
      key: 'approval_step',
      label: 'Approval Step',
      sortable: true,
      render: (value, row) => {
        // ✅ PERBAIKAN: Gunakan approval_step_name dari backend jika tersedia
        const stepName = row.approval_step_name || row.approvalStepName;

        // ✅ FALLBACK: Jika tidak ada nama dari backend, gunakan hardcoded mapping
        const stepLabels = {
          1: 'Manager Review',
          2: 'Director Review',
          3: 'Final Review'
        };

        const displayName = stepName || stepLabels[value] || `Step ${value}`;

        return (
          <span className="badge bg-info text-white">
            {displayName}
          </span>
        );
      }
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value, row) => {
        // Custom badge for NEED_APPROVAL
        if (value === 'NEED_APPROVAL') {
          return (
            <span
              className="badge bg-blue text-white"
              style={{ cursor: 'pointer' }}
              onClick={() => {
                // Navigate to proposal detail using leadId
                if (row.leadId) {
                  navigate(`/proposals/detail/${row.leadId}`);
                }
              }}
              onContextMenu={(e) => {
                e.preventDefault();
                // Open in new tab on right-click
                if (row.leadId) {
                  window.open(`/proposals/detail/${row.leadId}`, '_blank');
                }
              }}
            >
              {value}
            </span>
          );
        }
        return <span className="badge bg-secondary">{value}</span>;
      }
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      render: (value, row) => {
        return (
          <button
            className="btn btn-sm btn-primary"
            onClick={() => handleViewApprovalDetails(row)}
            title="View approval details"
          >
            View
          </button>
        );
      }
    }
  ];


  // Filter handler functions - memoized to prevent unnecessary re-renders
  const handleFilterChange = useCallback((filterType, value) => {
    // Block changes to locked filters
    if (filterType === 'people' && lockedStates.people) return;
    if (filterType === 'businessUnits' && lockedStates.businessUnits) return;

    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));

    // Show custom date picker when custom range is selected
    if (filterType === 'period' && value === 'custom') {
      setShowCustomDatePicker(true);
    } else if (filterType === 'period' && value !== 'custom') {
      setShowCustomDatePicker(false);
    }
  }, [lockedStates]);

  // Reset all non-locked filters back to default
  const handleResetFilters = useCallback(() => {
    setFilters(prev => ({
      ...prev,
      people: lockedStates.people ? prev.people : [],
      businessUnits: lockedStates.businessUnits ? prev.businessUnits : [],
      sector: [],
      standard: [],
      service: [],
      source: [],
      branchId: '',
      period: 'this_month'
    }));
    setCustomDateRange({ startDate: '', endDate: '' });
    setShowCustomDatePicker(false);
    setDateRangeError('');
  }, [lockedStates]);

  const handleRemoveDashboardFilter = useCallback((filterKey) => {
    if (filterKey === 'period') {
      handleFilterChange('period', 'this_month');
      return;
    }
    if (filterKey === 'branchId') {
      handleFilterChange('branchId', '');
      return;
    }
    handleFilterChange(filterKey, []);
  }, [handleFilterChange]);

  // Custom date range handlers
  const handleCustomDateChange = (dateType, value) => {
    setCustomDateRange(prev => {
      const newRange = {
        ...prev,
        [dateType]: value
      };
      
      // Clear any previous error
      setDateRangeError('');
      
      // Validate that start date is not later than end date
      if (newRange.startDate && newRange.endDate) {
        const startDate = new Date(newRange.startDate);
        const endDate = new Date(newRange.endDate);
        
        if (startDate > endDate) {
          setDateRangeError('Start date cannot be later than end date');
          // If start date is later than end date, clear the end date
          if (dateType === 'startDate') {
            newRange.endDate = '';
          } else {
            newRange.startDate = '';
          }
        }
      }
      
      return newRange;
    });
  };

  const handleApplyCustomDateRange = () => {
    if (customDateRange.startDate && customDateRange.endDate) {
      // Final validation before applying
      const startDate = new Date(customDateRange.startDate);
      const endDate = new Date(customDateRange.endDate);
      
      if (startDate > endDate) {
        setDateRangeError('Start date cannot be later than end date');
        return;
      }
      
      // ✅ PERBAIKAN: Update filters dan trigger data fetch
      const newFilters = {
        ...filters,
        period: 'custom'
      };
      
      setFilters(newFilters);
      setShowCustomDatePicker(false);
      setDateRangeError('');
      
      // ✅ PERBAIKAN: Fetch data dan total counts dengan custom date range (same filters = correct card counts)
      fetchDashboardData(newFilters);
      fetchTotalCounts(newFilters);
    }
  };

  const handleCancelCustomDateRange = () => {
    setShowCustomDatePicker(false);
    setCustomDateRange({
      startDate: '',
      endDate: ''
    });
    setDateRangeError('');
  };

  const handleOpenFilter = () => {
    const el = document.getElementById('offcanvasDashboard');
    if (!el || !window.bootstrap?.Offcanvas) return;
    offcanvasRef.current = window.bootstrap.Offcanvas.getOrCreateInstance(el, { backdrop: true, keyboard: true, scroll: false });
    offcanvasRef.current.show();
  };

  // ── People options filtered by currently selected departments ──────────
  const cascadedPeopleOptions = useMemo(() => {
    const selectedDepts = Array.isArray(filters.businessUnits)
      ? filters.businessUnits.map(String).filter(Boolean)
      : (filters.businessUnits && filters.businessUnits !== 'all'
          ? [String(filters.businessUnits)]
          : []);
    if (selectedDepts.length === 0) return resolvedPeopleOptions;
    return resolvedPeopleOptions.filter(u => {
      const deptId = u?.departmentId ?? u?.department_id ?? u?.department?.id;
      return deptId != null && selectedDepts.includes(String(deptId));
    });
  }, [resolvedPeopleOptions, filters.businessUnits]);

  // ── AdvancedFilterPanel adapter ───────────────────────────────────────
  // Department is first so selecting a department automatically narrows the People list.
  const dashboardFilterFields = useMemo(() => ([
    { key: 'businessUnits', label: 'Department', type: 'multiselect', options: resolvedBusinessUnitsOptions, valueField: 'id', labelField: 'name', disabled: isBusinessUnitsLocked },
    { key: 'people',        label: 'People',     type: 'multiselect', options: cascadedPeopleOptions,        valueField: 'id', labelField: 'name', disabled: isPeopleLocked },
    { key: 'sector',        label: 'Sector',     type: 'multiselect', options: sectorsData   || [],          valueField: 'id', labelField: 'name' },
    { key: 'standard',      label: 'Standard',   type: 'multiselect', options: standardsData || [],          valueField: 'id', labelField: 'name' },
    { key: 'service',       label: 'Service',    type: 'multiselect', options: servicesData  || [],          valueField: 'id', labelField: 'name' },
    { key: 'source',        label: 'Source',     type: 'multiselect', options: sourcesData   || [],          valueField: 'id', labelField: 'name' },
    { key: 'branchId',      label: 'Branch',     type: 'select',      options: branches      || [],          valueField: 'id', labelField: 'name' },
  ]), [cascadedPeopleOptions, resolvedBusinessUnitsOptions, sectorsData, standardsData, servicesData, sourcesData, branches, isPeopleLocked, isBusinessUnitsLocked]);

  const dashboardFilterData = useMemo(() => ({
    ...filters,
    startDate: customDateRange.startDate || '',
    endDate:   customDateRange.endDate   || '',
  }), [filters, customDateRange]);

  const handleDashboardFilterFieldChange = useCallback((key, val) => {
    if (key === 'startDate' || key === 'endDate') {
      handleCustomDateChange(key, val);
      return;
    }
    if (key === 'period') {
      if (val !== 'custom') {
        setCustomDateRange({ startDate: '', endDate: '' });
        setDateRangeError('');
      }
      handleFilterChange('period', val);
      return;
    }
    if (key === 'businessUnits') {
      // When department changes, reset People if the currently selected person
      // is no longer in any of the newly selected departments.
      handleFilterChange(key, val ?? '');
      const newDepts = Array.isArray(val) ? val.map(String) : (val && val !== 'all' ? [String(val)] : []);
      if (newDepts.length > 0 && !isPeopleLocked) {
        setFilters(prev => {
          const currentPeople = Array.isArray(prev.people) ? prev.people : (prev.people ? [prev.people] : []);
          if (currentPeople.length === 0) return prev;
          const stillValid = currentPeople.filter(pid => {
            const u = (salesUsersData || []).find(u => String(u?.id) === String(pid));
            if (!u) return false;
            const deptId = u?.departmentId ?? u?.department_id ?? u?.department?.id;
            return deptId != null && newDepts.includes(String(deptId));
          });
          return stillValid.length === currentPeople.length ? prev : { ...prev, people: stillValid };
        });
      }
      return;
    }
    // For single-select branchId, val may be '' to clear
    handleFilterChange(key, val ?? '');
  }, [handleFilterChange, isPeopleLocked, salesUsersData]);

  const handleDashboardApply = useCallback(() => {
    if (filters.period === 'custom' && customDateRange.startDate && customDateRange.endDate) {
      handleApplyCustomDateRange();
    }
  }, [filters.period, customDateRange, handleApplyCustomDateRange]);

  // Leads form handlers
  const handleCreateLeads = () => {
    resetForm();
    setIsLeadsModalOpen(true);
  };
  const handleViewTask = () => {
    navigate('/task');
  };

  const handleLeadsModalClose = () => {
    setIsLeadsModalOpen(false);
    resetForm();
  };

  const handleLeadsSuccessSubmit = (error) => {
    if (error) {
      setAlert({
        show: true,
        type: 'danger',
        message: 'Failed to create lead. Please try again.'
      });
    } else {
      setAlert({
        show: true,
        type: 'success',
        message: 'New lead created successfully!'
      });
      // Refresh dashboard data to show updated statistics
      fetchDashboardData(filters);
    }
    
    // Hide alert after 3 seconds
    setTimeout(() => {
      setAlert({ show: false, type: '', message: '' });
    }, 3000);
    
    setIsLeadsModalOpen(false);
  };

  // Pagination handlers for approval requests
  const handleApprovalPageChange = (page) => {
    // If we have all data stored (server doesn't paginate), use client-side pagination
    if (allApprovalRequests.length > 0 && (!approvalSearchQuery || !approvalSearchQuery.trim())) {
      const rowsPerPage = approvalPagination.rowsPerPage;
      const startIndex = (page - 1) * rowsPerPage;
      const endIndex = startIndex + rowsPerPage;
      const paginatedData = allApprovalRequests.slice(startIndex, endIndex);
      
      setApprovalRequestsData(paginatedData);
      setApprovalPagination(prev => ({ ...prev, currentPage: page }));
    } else {
      // Use server-side pagination
      setApprovalPagination(prev => ({ ...prev, currentPage: page }));
      fetchApprovalRequests(page, approvalPagination.rowsPerPage, approvalSearchQuery);
    }
  };

  const handleApprovalRowsPerPageChange = (size) => {
    // If we have all data stored (server doesn't paginate), use client-side pagination
    if (allApprovalRequests.length > 0 && (!approvalSearchQuery || !approvalSearchQuery.trim())) {
      const startIndex = 0;
      const endIndex = size;
      const paginatedData = allApprovalRequests.slice(startIndex, endIndex);
      
      setApprovalRequestsData(paginatedData);
      setApprovalPagination(prev => ({
        ...prev,
        rowsPerPage: size,
        currentPage: 1,
        totalPages: Math.ceil(allApprovalRequests.length / size)
      }));
    } else {
      // Use server-side pagination
      setApprovalPagination(prev => ({ ...prev, rowsPerPage: size, currentPage: 1 }));
      fetchApprovalRequests(1, size, approvalSearchQuery);
    }
  };


  // Search handlers for approval requests
  const handleApprovalSearchChange = (value) => {
    setApprovalSearchQuery(value);
  };

  const handleApprovalSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      // Always use server-side search when searching
      setApprovalPagination(prev => ({ ...prev, currentPage: 1 }));
      fetchApprovalRequests(1, approvalPagination.rowsPerPage, approvalSearchQuery);
    }
  };

  // Client-side search function for fallback
  const performClientSideSearch = (query) => {
    if (!query || !query.trim()) {
      return allApprovalRequests;
    }
    
    const searchTerm = query.toLowerCase().trim();
    return allApprovalRequests.filter(item => {
      return (
        (item.proposalcode && item.proposalcode.toLowerCase().includes(searchTerm)) ||
        (item.client && item.client.toLowerCase().includes(searchTerm)) ||
        (item.initiator && item.initiator.toLowerCase().includes(searchTerm)) ||
        (item.status && item.status.toLowerCase().includes(searchTerm)) ||
        (item.approval_step_name && item.approval_step_name.toLowerCase().includes(searchTerm)) ||
        (item.approvalStepName && item.approvalStepName.toLowerCase().includes(searchTerm)) ||
        (item.request_date && item.request_date.toLowerCase().includes(searchTerm)) ||
        (item.requestDate && item.requestDate.toLowerCase().includes(searchTerm))
      );
    });
  };

  // Clear search functionality
  const handleClearSearch = () => {
    setApprovalSearchQuery('');
    setApprovalPagination(prev => ({ ...prev, currentPage: 1 }));
    fetchApprovalRequests(1, approvalPagination.rowsPerPage, '');
  };

  const handleViewRequest = (item) => {
    // Handle view action
  };

  const handleEditRequest = (item) => {
    // Handle edit action
  };

  // ✅ TAMBAHAN: Handle viewing approval details
  const handleViewApprovalDetails = async (row) => {
    setLoadingApprovalDetails(true);
    setSelectedApproval(row);
    try {
      // Get leadId from the row
      const leadId = row.leadId || row.leadid;
      if (!leadId) {
        setAlert({ 
          show: true, 
          type: 'warning', 
          message: 'Lead ID not found for this approval request.' 
        });
        return;
      }

      // Fetch workflow items that need approval
      const workflowResponse = await api.get(`/crm/proposals/${leadId}/workflow-review`);
      const workflowItems = workflowResponse.data || [];
      setApprovalWorkflowItems(workflowItems);

      // Fetch note from proposal history
      try {
        // Get proposal ID from lead ID
        const proposalIdRes = await api.get(`/crm/proposals/id-by-lead/${leadId}`);
        const proposalId = typeof proposalIdRes.data === 'object' ? proposalIdRes.data.id : proposalIdRes.data;
        
        if (proposalId) {
          const historyRes = await api.get(`/crm/proposals/${proposalId}/history`);
          if (historyRes.data && Array.isArray(historyRes.data)) {
            // Find the most recent SENT type history entry with a note
            const sentHistory = historyRes.data
              .filter(h => h.type === 'SENT' || h.type === 'sent')
              .filter(h => h.notes && h.notes.includes('Note:'))
              .sort((a, b) => {
                const dateA = new Date(a.createdAt || a.created_at || 0);
                const dateB = new Date(b.createdAt || b.created_at || 0);
                return dateB - dateA;
              })[0];
            
            if (sentHistory && sentHistory.notes) {
              // Extract note text (text after "Note:")
              const noteMatch = sentHistory.notes.match(/Note:\s*(.+)/);
              if (noteMatch && noteMatch[1]) {
                setApprovalNote(noteMatch[1].trim());
              } else {
                setApprovalNote('');
              }
            } else {
              setApprovalNote('');
            }
          } else {
            setApprovalNote('');
          }
        } else {
          setApprovalNote('');
        }
      } catch (noteError) {
        console.warn('Could not fetch approval note:', noteError);
        setApprovalNote('');
      }

      setShowApprovalDetailModal(true);
    } catch (error) {
      console.error('Error fetching approval details:', error);
      setAlert({ 
        show: true, 
        type: 'danger', 
        message: 'Failed to fetch approval details: ' + (error.response?.data?.message || error.message)
      });
    } finally {
      setLoadingApprovalDetails(false);
    }
  };

  // ✅ TAMBAHAN: Handle closing approval detail modal
  const handleCloseApprovalDetailModal = () => {
    setShowApprovalDetailModal(false);
    setSelectedApproval(null);
    setApprovalWorkflowItems([]);
    setApprovalNote('');
  };

  // Helper function to build params for Prospects Reports
  // Shared helper: build report navigation URLSearchParams from current dashboard filter state.
  // All individual buildXxxReportsParams functions use this to avoid duplication.
  const _buildNavParams = (config) =>
    buildReportNavParams(serializedFilters, filters.period, customDateRange, config);

  const buildProspectsReportsParams = (filterType = null) =>
    _buildNavParams({ periodKey: 'period', extraParams: filterType ? { filterType } : {} });

  // Handler function to navigate to Prospects Reports with filters
  const handleNavigateToProspectsReports = (filterType = null) => {
    const params = buildProspectsReportsParams(filterType);
    const queryString = params.toString();
    navigate(`/reports/company${queryString ? `?${queryString}` : ''}`);
  };

  // Helper function to build params for Leads Reports
  const buildLeadsReportsParams = () =>
    _buildNavParams({ periodKey: 'period' });

  // Handler function to navigate to Leads Reports with filters
  const handleNavigateToLeadsReports = () => {
    const params = buildLeadsReportsParams();
    const queryString = params.toString();
    navigate(`/reports/leads${queryString ? `?${queryString}` : ''}`);
  };

  // Helper function to build params for Proposals Reports with proposalDate filter
  const buildProposalsReportsParams = () =>
    _buildNavParams({ periodKey: 'proposalDate', defaultPeriod: 'this_month' });

  // Handler function to navigate to Proposals Reports with filters
  // ✅ FIX: For "Converted to Proposal", filter by proposalDate (p.proposal_date field)
  const handleNavigateToProposalsReports = () => {
    const params = buildProposalsReportsParams();
    const queryString = params.toString();
    navigate(`/reports/proposals${queryString ? `?${queryString}` : ''}`);
  };

  // Helper function to build params for Proposals Reports with createdAt filter (for Sales Funnel Proposal)
  const buildProposalsReportsWithCreatedAtParams = () =>
    _buildNavParams({ periodKey: 'createdAt', defaultPeriod: 'this_month' });

  // Handler function to navigate to Proposals Reports with createdAt filter (for Sales Funnel Proposal)
  // ✅ FIX: Use createdAt filter (filters by p.created_at) to match Sales Funnel backend which uses created_at
  const handleNavigateToProposalsReportsWithCreatedAt = () => {
    const params = buildProposalsReportsWithCreatedAtParams();
    const queryString = params.toString();
    navigate(`/reports/proposals${queryString ? `?${queryString}` : ''}`);
  };

  // Helper function to build params for Projects Reports
  // ✅ Use projectStartDate filter (project start date), not createdAt (project created date)
  const buildProjectsReportsParams = () =>
    _buildNavParams({ periodKey: 'projectStartDate', defaultPeriod: 'this_month' });

  // Handler function to navigate to Projects Reports with filters
  const handleNavigateToProjectsReports = () => {
    const params = buildProjectsReportsParams();
    const queryString = params.toString();
    navigate(`/reports/projects${queryString ? `?${queryString}` : ''}`);
  };

  // Sales Funnel "Project" segment: filter Projects Report by Audit Date
  // (per-standard propose_date), NOT project start date. Scoped to the funnel
  // only — the "Total Projects" KPI card keeps using projectStartDate above.
  const buildProjectsReportsByAuditDateParams = () =>
    _buildNavParams({ periodKey: 'auditDate', defaultPeriod: 'this_month' });

  const handleNavigateToProjectsReportsByAuditDate = () => {
    const params = buildProjectsReportsByAuditDateParams();
    const queryString = params.toString();
    navigate(`/reports/projects${queryString ? `?${queryString}` : ''}`);
  };

  // Helper function to build params for Leads Reports with task update date filter
  const buildLeadsReportsWithTaskUpdateParams = () =>
    _buildNavParams({
      periodKey: 'taskUpdatePeriod',
      defaultPeriod: 'this_month',
      // ✅ FIX: Map people → taskUpdateBy, businessUnits → taskUpdateByDepartment
      peopleKey: 'taskUpdateBy',
      businessUnitsKey: 'taskUpdateByDepartment',
    });

  // Handler function to navigate to Leads Reports with task update date filter
  const handleNavigateToLeadsReportsWithTaskUpdate = () => {
    const params = buildLeadsReportsWithTaskUpdateParams();
    const queryString = params.toString();
    navigate(`/reports/leads${queryString ? `?${queryString}` : ''}`);
  };

  // Helper function to build params for Leads Reports with lead update date filter
  const buildLeadsReportsWithLeadUpdateParams = () =>
    _buildNavParams({
      periodKey: 'leadUpdatePeriod',
      defaultPeriod: 'this_month',
      // ✅ Map people → assignedTo (assigned user), keep businessUnits as-is
      peopleKey: 'assignedTo',
    });

  // Handler function to navigate to Leads Reports with lead update date filter
  // ✅ FOCUS: Assigned user and lead update date
  const handleNavigateToLeadsReportsWithLeadUpdate = () => {
    const params = buildLeadsReportsWithLeadUpdateParams();
    const queryString = params.toString();
    navigate(`/reports/leads${queryString ? `?${queryString}` : ''}`);
  };

  // Helper function to build params for Projects Reports (Converted to Project)
  // ✅ Use createdAt filter (project creation date) - keep as is per user request
  const buildProjectsReportsConvertedParams = () =>
    _buildNavParams({ periodKey: 'createdAt', defaultPeriod: 'this_month' });

  const buildProposalsReportsConvertedParams = () =>
    _buildNavParams({
      periodKey: 'createdAt',
      defaultPeriod: 'this_month',
      extraParams: { projectStatus: 'converted' },
    });

  const handleNavigateToProposalsReportsConverted = () => {
    const params = buildProposalsReportsConvertedParams();
    const queryString = params.toString();
    navigate(`/reports/proposals${queryString ? `?${queryString}` : ''}`);
  };

  const buildProposalsReportsNotConvertedParams = () =>
    _buildNavParams({
      periodKey: 'createdAt',
      defaultPeriod: 'this_month',
      extraParams: { projectStatus: 'not converted' },
    });

  const handleNavigateToProposalsReportsNotConverted = () => {
    const params = buildProposalsReportsNotConvertedParams();
    const queryString = params.toString();
    navigate(`/reports/proposals${queryString ? `?${queryString}` : ''}`);
  };

  // Helper function to build params for Proposals Reports with update date filter
  const buildProposalsReportsWithUpdateParams = () =>
    _buildNavParams({
      periodKey: 'updatedAt',
      defaultPeriod: 'this_month',
      // ✅ FIX: Use updatedAt to match backend getLatestUpdatedProposal() which uses p.udate
    });

  // Handler function to navigate to Proposals Reports with update date filter
  // ✅ FIX: Use updatedAt filter (filters by p.udate) to match backend getLatestUpdatedProposal()
  const handleNavigateToProposalsReportsWithUpdate = () => {
    const params = buildProposalsReportsWithUpdateParams();
    const queryString = params.toString();
    navigate(`/reports/proposals${queryString ? `?${queryString}` : ''}`);
  };

  // Handler function to navigate to Proposals Reports with proposal stage filter using proposalDate
  const handleNavigateToProposalsReportsWithStageAndProposalDate = (stageId) => {
    const params = _buildNavParams({
      periodKey: 'proposalDate',
      defaultPeriod: 'this_month',
      // ✅ FIX: Use proposalDate (p.proposal_date field, not p.created_at)
      extraParams: { proposalStage: stageId.toString() },
    });
    navigate(`/reports/proposals${params.toString() ? `?${params.toString()}` : ''}`);
  };

  // Handler function to navigate based on Sales Funnel category click
  const handleSalesFunnelClick = (category, categoryIndex) => {
    switch (category) {
      case 'Leads':
        handleNavigateToLeadsReports();
        break;
      case 'Proposal':
        // Sales Funnel Proposal uses createdAt to match backend p.created_at basis.
        handleNavigateToProposalsReportsWithCreatedAt();
        break;
      case 'Negotiation':
        // ✅ FIX: Use stageUpdatePeriod instead of proposalDate to match backend which now uses stage_updated_at
        handleNavigateToProposalsReportsWithStage(1);
        break;
      case 'Contract/Won':
        // Contract/Won shows proposals with Won status (proposal_stage_id = 5)
        // ✅ FIX: Use stageUpdatePeriod instead of proposalDate to match backend which now uses stage_updated_at
        handleNavigateToProposalsReportsWithStage(5);
        break;
      case 'Project':
        // Project segment filters the Projects Report by Audit Date (propose_date)
        handleNavigateToProjectsReportsByAuditDate();
        break;
      default:
        break;
    }
  };

  // Helper function to build params for Proposals Reports with proposal stage filter
  const buildProposalsReportsWithStageParams = (stageId) =>
    _buildNavParams({
      periodKey: 'stageUpdatePeriod',
      defaultPeriod: 'this_month',
      // ✅ FIX: Use stageUpdatePeriod to match backend which filters by stage_updated_at
      extraParams: { proposalStage: stageId.toString() },
    });

  // Handler function to navigate to Proposals Reports with proposal stage filter
  const handleNavigateToProposalsReportsWithStage = (stageId) => {
    const params = buildProposalsReportsWithStageParams(stageId);
    const queryString = params.toString();
    navigate(`/reports/proposals${queryString ? `?${queryString}` : ''}`);
  };

  // Handler function to navigate to Task page with task filter
  const handleNavigateToTask = (task) => {
    const params = new URLSearchParams();

    // ✅ FIX: Filter by task type using title case format
    // Task management items represent task types, not statuses, so only filter by type
    if (task.title) {
      // Map display names back to their stored type values (now in title case)
      const titleToTypeMap = {
        'Follow Up Calls': 'Follow Up',
        'Follow Up Email': 'Follow Up Email',
        'Follow Up Meeting': 'Follow Up Meeting',
        'Follow Up Call': 'Follow Up Call',
        'Proposal Documentation': 'Proposal',
        'Contract Review': 'Contract',
        'Client Meetings': 'Meeting',
        'Reminder': 'Reminder',
        'Email': 'Email',
        'Call': 'Call',
        'Telegram': 'Telegram',
        'Other Tasks': 'Other'
      };

      // Try exact match first
      let taskType = titleToTypeMap[task.title];

      // If no exact match, try to derive it from the title
      if (!taskType) {
        // Remove common suffixes and standardize
        const normalizedTitle = task.title.toLowerCase()
          .replace(' calls', '')
          .replace(' call', '')
          .replace(' documentation', '')
          .replace(' review', '')
          .replace(' meetings', '')
          .replace(' meeting', '')
          .replace(' tasks', '')
          .trim();

        // Convert back to title case
        taskType = normalizedTitle.split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      }

      if (taskType) {
        params.append('type', taskType);
      }
    }

    // Note: Task management items represent task TYPES, not statuses.
    // Do not filter by status when clicking task management items.
    // The task.status field contains display text like "5 tasks", not actual task statuses.

    // Add due date range filter based on dashboard period
    if (filters.period && filters.period !== 'all') {
      if (filters.period === 'custom' && customDateRange.startDate && customDateRange.endDate) {
        params.append('dueDateFrom', customDateRange.startDate);
        params.append('dueDateTo', customDateRange.endDate);
      } else {
        const { startDate: dateFrom, endDate: dateTo } = getPeriodDateRange(filters.period);
        if (dateFrom) params.append('dueDateFrom', dateFrom);
        if (dateTo) params.append('dueDateTo', dateTo);
      }
    }
    
    // ✅ Add sector, source, and standard filters from dashboard
    if (serializedFilters.sector !== 'all') {
      params.append('sector', serializedFilters.sector);
    }
    if (serializedFilters.source !== 'all') {
      params.append('source', serializedFilters.source);
    }
    if (serializedFilters.standard !== 'all') {
      params.append('standard', serializedFilters.standard);
    }
    if (serializedFilters.service !== 'all') {
      params.append('service', serializedFilters.service);
    }

    const queryString = params.toString();
    navigate(`/task${queryString ? `?${queryString}` : ''}`);
  };

  // Handler function to navigate to Task page with task type and status filter
  const handleNavigateToTaskWithStatus = (task, status) => {
    const params = new URLSearchParams();

    // Filter by task type using title case format
    if (task.title) {
      // Map display names back to their stored type values (now in title case)
      const titleToTypeMap = {
        'Follow Up Calls': 'Follow Up',
        'Follow Up Email': 'Follow Up Email',
        'Follow Up Meeting': 'Follow Up Meeting',
        'Follow Up Call': 'Follow Up Call',
        'Proposal Documentation': 'Proposal',
        'Contract Review': 'Contract',
        'Client Meetings': 'Meeting',
        'Reminder': 'Reminder',
        'Email': 'Email',
        'Call': 'Call',
        'Telegram': 'Telegram',
        'Other Tasks': 'Other'
      };

      // Try exact match first
      let taskType = titleToTypeMap[task.title];

      // If no exact match, try to derive it from the title
      if (!taskType) {
        // Remove common suffixes and standardize
        const normalizedTitle = task.title.toLowerCase()
          .replace(' calls', '')
          .replace(' call', '')
          .replace(' documentation', '')
          .replace(' review', '')
          .replace(' meetings', '')
          .replace(' meeting', '')
          .replace(' tasks', '')
          .trim();

        // Convert back to title case
        taskType = normalizedTitle.split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      }

      if (taskType) {
        params.append('type', taskType);
      }
    }

    // Add status filter
    if (status) {
      params.append('status', status);
    }

    // Add due date range filter based on dashboard period (for consistency)
    if (filters.period && filters.period !== 'all') {
      if (filters.period === 'custom' && customDateRange.startDate && customDateRange.endDate) {
        params.append('dueDateFrom', customDateRange.startDate);
        params.append('dueDateTo', customDateRange.endDate);
      } else {
        const { startDate: dateFrom, endDate: dateTo } = getPeriodDateRange(filters.period);
        if (dateFrom) params.append('dueDateFrom', dateFrom);
        if (dateTo) params.append('dueDateTo', dateTo);
      }
    }

    // ✅ Add sector, source, and standard filters from dashboard
    if (serializedFilters.sector !== 'all') {
      params.append('sector', serializedFilters.sector);
    }
    if (serializedFilters.source !== 'all') {
      params.append('source', serializedFilters.source);
    }
    if (serializedFilters.standard !== 'all') {
      params.append('standard', serializedFilters.standard);
    }
    if (serializedFilters.service !== 'all') {
      params.append('service', serializedFilters.service);
    }

    navigate(`/task${params.toString() ? `?${params}` : ''}`);
  };

  // Handler function to navigate to Task page with team member filter
  const handleNavigateToTaskWithTeamMember = (teamMemberName, categoryIndex) => {
    // Get user ID from teamMemberData if available
    let userId = null;
    if (dashboardData.teamMemberData.userIds && Array.isArray(dashboardData.teamMemberData.userIds)) {
      // Ensure categoryIndex is a number
      const index = typeof categoryIndex === 'number' ? categoryIndex : parseInt(categoryIndex, 10);
      if (!isNaN(index) && index >= 0 && index < dashboardData.teamMemberData.userIds.length) {
        userId = dashboardData.teamMemberData.userIds[index];
      }
    }
    
    // Fallback: If userIds array is not available, try to find user ID from salesUsersData by name
    if (!userId && teamMemberName) {
      const user = salesUsersData.find(u => {
        const fullName = u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim();
        return fullName === teamMemberName;
      });
      if (user && user.id) {
        userId = user.id;
      }
    }
    
    // Build URL with filters
    const params = new URLSearchParams();
    
    // Add assigned user filter if we have the user ID
    if (userId) {
      params.append('assignedUser', userId.toString());
    }
    
    // Add status filter to show only 'in progress' and 'done' tasks (matching the chart data)
    params.append('status', 'in progress,done');
    
    // Add date range filter based on dashboard period
    if (filters.period && filters.period !== 'all') {
      if (filters.period === 'custom' && customDateRange.startDate && customDateRange.endDate) {
        params.append('dateFrom', customDateRange.startDate);
        params.append('dateTo', customDateRange.endDate);
      } else {
        const { startDate: dateFrom, endDate: dateTo } = getPeriodDateRange(filters.period);
        if (dateFrom) params.append('dateFrom', dateFrom);
        if (dateTo) params.append('dateTo', dateTo);
      }
    }

    // ✅ Add sector, source, and standard filters from dashboard
    if (serializedFilters.sector !== 'all') {
      params.append('sector', serializedFilters.sector);
    }
    if (serializedFilters.source !== 'all') {
      params.append('source', serializedFilters.source);
    }
    if (serializedFilters.standard !== 'all') {
      params.append('standard', serializedFilters.standard);
    }
    if (serializedFilters.service !== 'all') {
      params.append('service', serializedFilters.service);
    }

    const queryString = params.toString();
    navigate(`/task${queryString ? `?${queryString}` : ''}`);
  };

  // Handler function to handle Proposal Breakdown chart clicks
  const handleProposalBreakdownClick = (label, index, value) => {
    // Proposal Breakdown uses `p.status_proposal_desc` as labels; reports expect numeric proposalStatus ids.
    const statusId = proposalBreakdownLabelToProposalStatusId(label);
    handleNavigateToProposalsReportsWithStatus('proposal', statusId, true);
  };

  // Handler function to handle Leads by Source chart clicks
  const handleLeadsBySourceClick = (label, index, value) => {
    // Find the source ID from sourcesData by matching the source name
    const source = sourcesData.find(s => s.name === label);
    const sourceId = source ? source.id : 'all';
    
    // Navigate to Leads Reports with source filter
    const params = new URLSearchParams();
    
    // Add current filters to URL params (Leads Reports uses: people, businessUnits, sector, source, standard, period)
    if (serializedFilters.people !== 'all') {
      params.append('people', serializedFilters.people);
    }
    if (serializedFilters.businessUnits !== 'all') {
      params.append('businessUnits', serializedFilters.businessUnits);
    }
    if (serializedFilters.sector !== 'all') {
      params.append('sector', serializedFilters.sector);
    }
    // Add the clicked source filter
    if (sourceId && sourceId !== 'all') {
      params.append('source', sourceId);
    }
    if (serializedFilters.standard !== 'all') {
      params.append('standard', serializedFilters.standard);
    }
    if (serializedFilters.service !== 'all') {
      params.append('service', serializedFilters.service);
    }
    if (filters.period && filters.period !== 'all') {
      params.append('period', filters.period);
    }
    
    // Add custom date range if period is custom
    if (filters.period === 'custom' && customDateRange.startDate && customDateRange.endDate) {
      params.append('customStartDate', customDateRange.startDate);
      params.append('customEndDate', customDateRange.endDate);
    }
    
    const queryString = params.toString();
    navigate(`/reports/leads${queryString ? `?${queryString}` : ''}`);
  };

  // Handler function to handle Leads by Industry Sector chart clicks
  const handleLeadsByIndustryClick = (label, index, value) => {
    // Delay navigation slightly to let ApexCharts finish processing the click event
    setTimeout(() => {
      // Find the sector ID from sectorsData by matching the sector name
      const sector = sectorsData.find(s => s.name === label);
      const sectorId = sector ? sector.id : 'all';
      
      // Navigate to Leads Reports with sector filter
      const params = new URLSearchParams();
      
      // Add current filters to URL params (Leads Reports uses: people, businessUnits, source, period)
      if (serializedFilters.people !== 'all') {
        params.append('people', serializedFilters.people);
      }
      if (serializedFilters.businessUnits !== 'all') {
        params.append('businessUnits', serializedFilters.businessUnits);
      }
      if (serializedFilters.source !== 'all') {
        params.append('source', serializedFilters.source);
      }
      // Add the clicked sector filter
      if (sectorId && sectorId !== 'all') {
        params.append('sector', sectorId);
      }
      if (serializedFilters.standard !== 'all') {
        params.append('standard', serializedFilters.standard);
      }
      if (serializedFilters.service !== 'all') {
        params.append('service', serializedFilters.service);
      }
      if (filters.period && filters.period !== 'all') {
        params.append('period', filters.period);
      }
      
      // Add custom date range if period is custom
      if (filters.period === 'custom' && customDateRange.startDate && customDateRange.endDate) {
        params.append('customStartDate', customDateRange.startDate);
        params.append('customEndDate', customDateRange.endDate);
      }

      const queryString = params.toString();
      navigate(`/reports/leads${queryString ? `?${queryString}` : ''}`);
    }, 100);
  };

  // Helper function to build params for Proposals Reports with status filter
  const buildProposalsReportsWithStatusParams = (statusType, statusValue, useCreatedAt = false) => {
    // ✅ FIX: Use createdAt for Proposal Breakdown (p.created_at), proposalDate for KPI drill-down (p.proposal_date)
    // statusValue is numeric id (same as the Proposals list / Proposals Reports options).
    const v = statusValue != null && statusValue !== '' ? String(statusValue) : '';
    const statusExtraParam = statusType === 'approval'
      ? { statusApproval: v }
      : statusType === 'proposal'
        ? { statusProposal: v }
        : {};
    return _buildNavParams({
      periodKey: useCreatedAt ? 'createdAt' : 'proposalDate',
      defaultPeriod: 'this_month',
      extraParams: statusExtraParam,
    });
  };

  // Handler function to navigate to Proposals Reports with status filter
  // ✅ FIX: For Proposal Breakdown, use createdAt filter to match backend (p.created_at)
  // For other status filters, use proposalDate filter (p.proposal_date)
  const handleNavigateToProposalsReportsWithStatus = (statusType, statusValue, useCreatedAt = false) => {
    const params = buildProposalsReportsWithStatusParams(statusType, statusValue, useCreatedAt);
    const queryString = params.toString();
    navigate(`/reports/proposals${queryString ? `?${queryString}` : ''}`);
  };

  // Function to check if due date is past and return appropriate className
  const getRowClassName = (item) => {
    // Get due date from various possible field names
    const dueDate = item.duedate || item.dueDate || item.due_date;
    
    if (!dueDate) return '';
    
    try {
      const dueDateObj = new Date(dueDate);
      const today = new Date();
      // Set time to midnight for accurate date comparison
      today.setHours(0, 0, 0, 0);
      dueDateObj.setHours(0, 0, 0, 0);
      
      // If due date is in the past, return red background class
      if (dueDateObj < today) {
        return 'row-past-due'; // Custom class for past due dates
      }
    } catch (error) {
      console.warn('Error parsing due date:', error);
    }
    
    return '';
  };

  // Helper functions for task display
  const getPriorityBadge = (priority) => {
    if (!priority) return 'badge badge-outline text-gray';
    const lowerPriority = priority.toLowerCase();
    const colors = {
      'high': 'bg-danger text-white',
      'medium': 'bg-warning text-white',
      'low': 'bg-info text-white'
    };
    return `badge ${colors[lowerPriority] || 'badge-outline text-gray'}`;
  };

  const getTaskStatusBadge = (status) => {
    if (!status) return 'badge badge-outline text-gray';
    const lowerStatus = status.toLowerCase();
    const colors = {
      'done': 'badge badge-outline text-success',
      'to do': 'badge badge-outline text-primary',
      'in progress': 'badge badge-outline text-warning'
    };
    return `badge ${colors[lowerStatus] || 'badge-outline text-gray'}`;
  };

  const formatTaskDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric' 
      });
    } catch (e) {
      return dateString;
    }
  };

  const isTaskOverdue = (dueDate) => {
    if (!dueDate) return false;
    try {
      const due = new Date(dueDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      due.setHours(0, 0, 0, 0);
      return due < today;
    } catch (e) {
      return false;
    }
  };

  // If user has no view permissions, hide the entire dashboard
  if (viewScope === 'none' && !capabilitiesLoading) {
    return (
      <div className="container-xl">
        <div className="row g-2">
          <div className="col-md-2">
            <div className="page-pretitle">Overview</div>
            <h2 className="page-title">Dashboard</h2>
          </div>
          <div className="col-md-10">
            <div className={`card${hasActiveFilters ? ' stat-card-filtered' : ''}`}>
              <div className="card-body text-center py-5">
                <div className="empty-state">
                  <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-lg text-muted mb-3" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                    <path d="M14 3v4a1 1 0 0 0 1 1h4"/>
                    <path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z"/>
                    <path d="M9 17h6"/>
                    <path d="M9 13h6"/>
                  </svg>
                  <h3 className="text-muted">Access Denied</h3>
                  <p className="text-muted">You don't have permission to view dashboard data. Please contact your administrator to grant appropriate permissions.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-xl">
      {/* Custom CSS for the inline filter selects */}
      <style>
        {`
          .dashboard-filters .mb-3 {
            margin-bottom: 0 !important;
          }
          /* Red background for rows with past due dates */
          .row-past-due {
            background-color: #ffebee !important;
          }
          .row-past-due:hover {
            background-color: #ffcdd2 !important;
          }
          /* Active filter chips bar */
          .dashboard-filter-chips {
            display: flex;
            flex-wrap: wrap;
            gap: 6px;
            align-items: center;
            padding: 8px 0 4px 0;
          }
          .filter-chip {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            padding: 3px 10px 3px 8px;
            border-radius: 20px;
            font-size: 12px;
            font-weight: 500;
            line-height: 1.5;
            cursor: default;
            user-select: none;
            transition: all 0.15s ease;
            border: 1px solid transparent;
          }
          .filter-chip-blue   { background: #dbeafe; color: #1d4ed8; border-color: #bfdbfe; }
          .filter-chip-purple { background: #ede9fe; color: #6d28d9; border-color: #ddd6fe; }
          .filter-chip-teal   { background: #ccfbf1; color: #0f766e; border-color: #99f6e4; }
          .filter-chip-orange { background: #ffedd5; color: #c2410c; border-color: #fed7aa; }
          .filter-chip-cyan   { background: #cffafe; color: #0e7490; border-color: #a5f3fc; }
          .filter-chip-green  { background: #dcfce7; color: #15803d; border-color: #bbf7d0; }
          .filter-chip-red    { background: #fee2e2; color: #b91c1c; border-color: #fecaca; }
          .filter-chip-close {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 14px;
            height: 14px;
            border-radius: 50%;
            background: rgba(0,0,0,0.12);
            cursor: pointer;
            margin-left: 2px;
            font-size: 10px;
            line-height: 1;
            flex-shrink: 0;
          }
          .filter-chip-close:hover {
            background: rgba(0,0,0,0.25);
          }
          .filter-reset-btn {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            padding: 3px 10px;
            border-radius: 20px;
            font-size: 12px;
            font-weight: 500;
            border: 1px dashed #cbd5e1;
            background: transparent;
            color: #64748b;
            cursor: pointer;
            transition: all 0.15s ease;
          }
          .filter-reset-btn:hover {
            background: #f1f5f9;
            border-color: #94a3b8;
            color: #334155;
          }
          .filter-active-label {
            font-size: 11px;
            color: #94a3b8;
            font-weight: 500;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            flex-shrink: 0;
          }
          /* Stat card filtered state */
          .stat-card-filtered {
            position: relative;
          }
          .stat-card-filtered::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            height: 3px;
            background: linear-gradient(90deg, #667eea, #764ba2);
            border-radius: 0 0 4px 4px;
            opacity: 0.7;
          }
          /* Loading skeleton pulse */
          @keyframes skeleton-pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.4; }
          }
          .stat-loading {
            display: inline-block;
            background: #e2e8f0;
            border-radius: 4px;
            animation: skeleton-pulse 1.5s ease-in-out infinite;
            min-width: 60px;
            height: 1.2em;
            vertical-align: middle;
          }
          .stat-loading-light {
            display: inline-block;
            background: rgba(255,255,255,0.35);
            border-radius: 4px;
            animation: skeleton-pulse 1.5s ease-in-out infinite;
            min-width: 60px;
            height: 1.2em;
            vertical-align: middle;
          }
          /* Filtered count highlight */
          .stat-filtered-count {
            position: relative;
          }
          .stat-filtered-count::after {
            content: 'filtered';
            position: absolute;
            top: -14px;
            left: 0;
            font-size: 9px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.07em;
            color: #667eea;
            line-height: 1;
          }
          /* Dashboard Info Tooltip */
          .dashboard-info-tooltip-wrapper {
            position: relative;
            display: inline-flex;
            align-items: center;
            margin-left: 4px;
          }
          .dashboard-info-tooltip-wrapper::after {
            content: attr(data-tooltip);
            position: absolute;
            bottom: calc(100% + 8px);
            left: 50%;
            transform: translateX(-50%);
            background: #1e293b;
            color: #f8fafc;
            font-size: 12px;
            font-weight: 400;
            line-height: 1.5;
            padding: 7px 12px;
            border-radius: 6px;
            white-space: nowrap;
            max-width: 260px;
            white-space: normal;
            text-align: left;
            z-index: 9999;
            pointer-events: none;
            opacity: 0;
            transition: opacity 0.18s ease;
            box-shadow: 0 4px 16px rgba(0,0,0,0.18);
          }
          .dashboard-info-tooltip-wrapper::before {
            content: '';
            position: absolute;
            bottom: calc(100% + 2px);
            left: 50%;
            transform: translateX(-50%);
            border: 6px solid transparent;
            border-top-color: #1e293b;
            z-index: 9999;
            pointer-events: none;
            opacity: 0;
            transition: opacity 0.18s ease;
          }
          .dashboard-info-tooltip-wrapper:hover::after,
          .dashboard-info-tooltip-wrapper:hover::before {
            opacity: 1;
          }
          .dashboard-info-icon:hover {
            color: #667eea !important;
            stroke: #667eea !important;
          }
        `}
      </style>
      {alert.show && (
        <Alert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert({ show: false, type: '', message: '' })}
        />
      )}
      <div className="row g-2 align-items-center">
        <div className="col">
          <div className="page-pretitle">Overview</div>
          <h2 className="page-title">Dashboard</h2>
        </div>
        <div className="col-auto ms-auto">
          <button type="button" className="btn" onClick={handleOpenFilter}>
            <Funnel className="icon icon-tabler text-black" />
            Filter
          </button>
        </div>
      </div>

      {/* Sticky awareness bar — sibling of page-body so its containing block spans the whole scroll range */}
      <FilterAwarenessBar
        title="CRM Dashboard is showing a filtered view"
        summary={`Filtered by ${activeFilterTags.length} ${activeFilterTags.length === 1 ? 'criterion' : 'criteria'}`}
        detail={activeFilterTags.map((tag) => tag.label).join(' · ')}
        loading={loading}
        activeCount={activeFilterTags.length}
        filterTags={activeFilterTags.map((tag) => ({ key: tag.key, label: `${tag.icon} ${tag.label}` }))}
        onRemoveFilter={handleRemoveDashboardFilter}
        onResetFilters={handleResetFilters}
      />
      
      {/* Custom Date Range Picker Modal */}
      {showCustomDatePicker && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Select Custom Date Range</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={handleCancelCustomDateRange}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label htmlFor="startDate" className="form-label">Start Date</label>
                    <input
                      type="date"
                      className="form-control"
                      id="startDate"
                      value={customDateRange.startDate}
                      onChange={(e) => handleCustomDateChange('startDate', e.target.value)}
                    />
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="endDate" className="form-label">End Date</label>
                    <input
                      type="date"
                      className="form-control"
                      id="endDate"
                      value={customDateRange.endDate}
                      onChange={(e) => handleCustomDateChange('endDate', e.target.value)}
                      min={customDateRange.startDate}
                    />
                  </div>
                </div>
                {dateRangeError && (
                  <div className="mt-3">
                    <div className="alert alert-danger" role="alert">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon alert-icon">
                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71 -3L13.71 3.86a2 2 0 0 0 -3.42 0z"></path>
                        <line x1="12" y1="9" x2="12" y2="13"></line>
                        <line x1="12" y1="17" x2="12.01" y2="17"></line>
                      </svg>
                      {dateRangeError}
                    </div>
                  </div>
                )}
                {customDateRange.startDate && customDateRange.endDate && !dateRangeError && (
                  <div className="mt-3">
                    <small className="text-muted">
                      Selected range: {new Date(customDateRange.startDate).toLocaleDateString()} - {new Date(customDateRange.endDate).toLocaleDateString()}
                    </small>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={handleCancelCustomDateRange}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-primary" 
                  onClick={handleApplyCustomDateRange}
                  disabled={!customDateRange.startDate || !customDateRange.endDate || dateRangeError}
                >
                  Apply Range
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Filter Offcanvas — shared AdvancedFilterPanel for consistent UX across all CRM modules */}
      <AdvancedFilterPanel
        offcanvasId="offcanvasDashboard"
        title="Filter"
        filterData={dashboardFilterData}
        onChange={handleDashboardFilterFieldChange}
        onApply={handleDashboardApply}
        onReset={handleResetFilters}
        filterFields={dashboardFilterFields}
        showPeriod
      />

      <div className="page-body">
        <div className="container-xl">
        {/* Only show approval requests section if user has can_approve capability */}
        {canApprove && (
          <div className="row mt-3 mb-3">
            <div className='col-md-12'>
              <div className='card'>
                <div className='card-body'>
                  <div className='row'>
                    <div className='col-md-6'>
                      <div className='card'>
                        <div className='card-body'>
                          <div className="row align-items-center">
                            <div className="col-auto">
                              <span className="bg-blue text-white avatar">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-users"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M9 7m-4 0a4 4 0 1 0 8 0a4 4 0 1 0 -8 0" /><path d="M3 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /><path d="M21 21v-2a4 4 0 0 0 -3 -3.85" /></svg></span>
                            </div>
                            <div className="col">
                              <div className="font-weight-medium ">Approval Request</div>
                              <div className="text-secondary">{totalApprovalRequests} waiting request{totalApprovalRequests !== 1 ? 's' : ''}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className='col-md-6'>
                      <div className='card'>
                        <div className='card-body'>
                          <div className="row align-items-center">
                            <div className="col-auto">
                              <span className="bg-yellow text-white avatar">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-message"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M8 9h8" /><path d="M8 13h6" /><path d="M18 4a3 3 0 0 1 3 3v8a3 3 0 0 1 -3 3h-5l-5 3v-3h-2a3 3 0 0 1 -3 -3v-8a3 3 0 0 1 3 -3h12z" /></svg></span>
                            </div>
                            <div className="col">
                              <div className="font-weight-medium">Revision with notes</div>
                              <div className="text-secondary">0 waiting request</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-12 mt-3">
                      <div className="d-flex align-items-center">
                        <div className="h3 text-primary mb-3">Approval Request</div>
                        <div className="ms-auto">
                          {/* <button className="btn btn-light text-muted">
                            View Task
                          </button> */}
                        </div>
                      </div>
                    </div>
                    <GenericView
                      data={approvalRequestsData}
                      columns={columns}
                      checkboxSelection={false}
                      tableClassName="table card-table table-vcenter text-nowrap"
                      isLoading={loading}
                      showTopControls={true}
                      rowClassName={getRowClassName}
                      searchComponent={
                        <SearchInput
                          value={approvalSearchQuery}
                          onChange={handleApprovalSearchChange}
                          onKeyPress={handleApprovalSearchKeyPress}
                          placeholder="Search approval requests..."
                          uniqueId="approval-search"
                        />
                      }
                      pagination={{
                        currentPage: approvalPagination.currentPage,
                        totalPages: approvalPagination.totalPages,
                        totalRows: approvalPagination.totalRows,
                        rowsPerPage: approvalPagination.rowsPerPage,
                        onPageChange: handleApprovalPageChange,
                        onRowsPerPageChange: handleApprovalRowsPerPageChange
                      }}
                      showPagination={true}
                      pageSizeOptions={[5, 10, 25, 50]}
                      emptyStateMessage="No approval requests found"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        
        {/* To Do and In Progress Tasks Section - HIDDEN */}
        {false && (
        <div className="row mt-3 mb-3">
          <div className="col-md-12">
            <div className={`card${hasActiveFilters ? ' stat-card-filtered' : ''}`}>
              <div className="card-header">
                <h3 className="card-title mb-0">Tasks (To Do & In Progress)</h3>
                <div className="card-actions">
                  <button 
                    className="btn btn-sm btn-primary"
                    onClick={() => navigate('/task?status=to do,in progress')}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      window.open('/task?status=to do,in progress', '_blank');
                    }}
                    title="View all tasks (Right-click to open in new tab)"
                  >
                    View All Tasks
                  </button>
                </div>
              </div>
              <div className="card-body">
                {loadingPriorityTasks ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2 text-muted">Loading tasks...</p>
                  </div>
                ) : priorityTasks.length === 0 ? (
                  <div className="text-center py-4 text-muted">
                    <p>No tasks found with "To Do" or "In Progress" status.</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-vcenter card-table">
                      <thead>
                        <tr>
                          <th>Priority</th>
                          <th>Title</th>
                          <th>Type</th>
                          <th>Status</th>
                          <th>Due Date</th>
                          <th>Assigned To</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {priorityTasks.map((task) => (
                          <tr key={task.id}>
                            <td>
                              <span className={getPriorityBadge(task.priority)}>
                                {task.priority ? task.priority.charAt(0).toUpperCase() + task.priority.slice(1) : 'N/A'}
                              </span>
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <div>
                                  <div className="font-weight-medium">
                                    {task.title || task.taskTitle || 'Untitled Task'}
                                  </div>
                                  {task.description && (
                                    <div className="text-muted small text-truncate" style={{ maxWidth: '300px' }}>
                                      {task.description}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className="text-muted">
                                {task.type ? task.type.charAt(0).toUpperCase() + task.type.slice(1) : '-'}
                              </span>
                            </td>
                            <td>
                              <span className={getTaskStatusBadge(task.status)}>
                                {task.status ? task.status.charAt(0).toUpperCase() + task.status.slice(1) : 'N/A'}
                              </span>
                            </td>
                            <td>
                              {task.dueDate ? (
                                <span className={isTaskOverdue(task.dueDate) ? 'text-danger fw-bold' : ''}>
                                  {formatTaskDate(task.dueDate)}
                                </span>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              <span className="text-muted">
                                {task.assignedUser || task.assignedUserName || '-'}
                              </span>
                            </td>
                            <td>
                              <button
                                className="btn btn-sm btn-primary"
                                onClick={() => navigate(`/task?id=${task.id}`)}
                                onContextMenu={(e) => {
                                  e.preventDefault();
                                  window.open(`/task?id=${task.id}`, '_blank');
                                }}
                                title="View task details (Right-click to open in new tab)"
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {/* Pagination for Priority Tasks */}
                {!loadingPriorityTasks && priorityTasks.length > 0 && (
                  <div className="d-flex align-items-center justify-content-between mt-3">
                    <div className="text-muted small">
                      Showing {((priorityTasksPagination.currentPage - 1) * priorityTasksPagination.rowsPerPage) + 1} to{' '}
                      {Math.min(priorityTasksPagination.currentPage * priorityTasksPagination.rowsPerPage, priorityTasksPagination.totalRows)} of{' '}
                      {priorityTasksPagination.totalRows} tasks
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <div className="d-flex align-items-center gap-1">
                        <label className="form-label mb-0 small">Rows per page:</label>
                        <select
                          className="form-select form-select-sm"
                          style={{ width: 'auto' }}
                          value={priorityTasksPagination.rowsPerPage}
                          onChange={(e) => handlePriorityTasksRowsPerPageChange(Number(e.target.value))}
                        >
                          <option value={5}>5</option>
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                        </select>
                      </div>
                      <div className="btn-group" role="group">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          disabled={priorityTasksPagination.currentPage === 1}
                          onClick={() => handlePriorityTasksPageChange(priorityTasksPagination.currentPage - 1)}
                        >
                          Previous
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          disabled={priorityTasksPagination.currentPage === priorityTasksPagination.totalPages}
                          onClick={() => handlePriorityTasksPageChange(priorityTasksPagination.currentPage + 1)}
                        >
                          Next
                        </button>
                      </div>
                      <div className="text-muted small">
                        Page {priorityTasksPagination.currentPage} of {priorityTasksPagination.totalPages}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        )}

          <div className="row g-1">
            {/* Total Item Card - Teal */}
            <div className="col-md-3">
              <div className={`card bg-primary text-white h-100${hasActiveFilters ? ' stat-card-filtered' : ''}`}>
                <div className="card-body d-flex flex-column justify-content-between">
                  {/* Top Content */}
                  <div>
                    <div className="d-flex align-items-center mb-3 gap-1">
                      <div className="subheader text-white">Total Prospect</div>
                      <InfoTooltip text="Total number of potential clients in the database." light />
                    </div>

                    <div className="mb-3">
                      <div 
                        className="h1" 
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleNavigateToProspectsReports()}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          const params = buildProspectsReportsParams();
                          const queryString = params.toString();
                        window.open(`/reports/company${queryString ? `?${queryString}` : ''}`, '_blank');
                        }}
                        title="Click to view Prospects Report (Right-click to open in new tab)"
                      >
                        <StatNum
                          loading={dashboardLoading}
                          filtered={dashboardData.statistics.totalProspect ?? 0}
                          total={totalCounts.totalProspect ?? 0}
                          isFiltered={hasActiveFilters}
                          light={true}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Bottom "Goals" Section */}
                  <div className="d-flex align-items-center mt-auto">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                      fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                      className="icon icon-tabler icons-tabler-outline icon-tabler-star me-1">
                      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                      <path d="M12 17.75l-6.172 3.245l1.179 -6.873l-5 -4.867l6.9 -1l3.086 -6.253l3.086 6.253l6.9 1l-5 4.867l1.179 6.873z" />
                    </svg>
                    <div className="text-white">Goals</div>
                  </div>
                </div>
              </div>
            </div>
            {/* ITEM Cards - White 2x2 Grid */}
            <div className="col-md-6">
              <div className="row g-1">
                <div className="col-md-6">
                  <div className={`card${hasActiveFilters ? ' stat-card-filtered' : ''}`}>
                    <div className="card-body">
                      <div className="d-flex align-items-center gap-1">
                        <div className="subheader">New Prospect</div>
                        <InfoTooltip text="Number of prospects added in the current month." />
                      </div>
                      <div className="mt-3">
                        <div
                          className="h1"
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleNavigateToProspectsReports('new')}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            const params = buildProspectsReportsParams('new');
                            const queryString = params.toString();
                        window.open(`/reports/company${queryString ? `?${queryString}` : ''}`, '_blank');
                          }}
                          title="Click to view New Prospects Report (Right-click to open in new tab)"
                        >
                          <StatNum
                          loading={dashboardLoading}
                          filtered={dashboardData.statistics.newProspect ?? 0}
                          total={totalCounts.newProspect ?? 0}
                          isFiltered={hasActiveFilters}
                          light={false}
                        />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className={`card${hasActiveFilters ? ' stat-card-filtered' : ''}`}>
                    <div className="card-body">
                      <div className="d-flex align-items-center gap-1">
                        <div className="subheader">Converted to Proposal</div>
                        <InfoTooltip text="Number of leads that have progressed to the proposal stage." />
                      </div>
                      <div className="mt-3">
                        <div 
                          className="h1"
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleNavigateToProposalsReports()}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            const params = buildProposalsReportsParams();
                            const queryString = params.toString();
                            window.open(`/reports/proposals${queryString ? `?${queryString}` : ''}`, '_blank');
                          }}
                          title="Click to view Proposals Report - Proposals with proposal date in selected period (Right-click to open in new tab)"
                        >
                          <StatNum
                          loading={dashboardLoading}
                          filtered={dashboardData.statistics.convertedToProposal ?? 0}
                          total={totalCounts.convertedToProposal ?? 0}
                          isFiltered={hasActiveFilters}
                          light={false}
                        />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className={`card${hasActiveFilters ? ' stat-card-filtered' : ''}`}>
                    <div className="card-body">
                      <div className="d-flex align-items-center gap-1">
                        <div className="subheader">Total Leads</div>
                        <InfoTooltip text="Total number of identified sales opportunities." />
                      </div>
                      <div className="mt-3">
                        <div 
                          className="h1"
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleNavigateToLeadsReports()}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            const params = buildLeadsReportsParams();
                            const queryString = params.toString();
                            window.open(`/reports/leads${queryString ? `?${queryString}` : ''}`, '_blank');
                          }}
                          title="Click to view Leads Report (Right-click to open in new tab)"
                        >
                          <StatNum
                          loading={dashboardLoading}
                          filtered={dashboardData.statistics.totalLeads ?? 0}
                          total={totalCounts.totalLeads ?? 0}
                          isFiltered={hasActiveFilters}
                          light={false}
                        />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className={`card${hasActiveFilters ? ' stat-card-filtered' : ''}`}>
                    <div className="card-body">
                      <div className="d-flex align-items-center gap-1">
                        <div className="subheader">Total Projects</div>
                        <InfoTooltip text="Total number of projects currently in progress or completed." />
                      </div>
                      <div className="mt-3">
                        <div 
                          className="h1"
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleNavigateToProjectsReports()}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            const params = buildProjectsReportsParams();
                            const queryString = params.toString();
                            window.open(`/reports/projects${queryString ? `?${queryString}` : ''}`, '_blank');
                          }}
                          title="Click to view Projects Report (Right-click to open in new tab)"
                        >
                          <StatNum
                          loading={dashboardLoading}
                          filtered={dashboardData.statistics.totalProjects ?? 0}
                          total={totalCounts.totalProjects ?? 0}
                          isFiltered={hasActiveFilters}
                          light={false}
                        />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Quick Actions Card */}
            <div className="col-md-3">
              <div className={`card h-100${hasActiveFilters ? ' stat-card-filtered' : ''}`}>
                <div className="card-body d-flex flex-column justify-content-start">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <h6 className="card-title text-muted mb-0">Quick Actions</h6>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="grey" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-settings"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M10.325 4.317c.426 -1.756 2.924 -1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543 -.94 3.31 .826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756 .426 1.756 2.924 0 3.35a1.724 1.724 0 0 0 -1.066 2.573c.94 1.543 -.826 3.31 -2.37 2.37a1.724 1.724 0 0 0 -2.572 1.065c-.426 1.756 -2.924 1.756 -3.35 0a1.724 1.724 0 0 0 -2.573 -1.066c-1.543 .94 -3.31 -.826 -2.37 -2.37a1.724 1.724 0 0 0 -1.065 -2.572c-1.756 -.426 -1.756 -2.924 0 -3.35a1.724 1.724 0 0 0 1.066 -2.573c-.94 -1.543 .826 -3.31 2.37 -2.37c1 .608 2.296 .07 2.572 -1.065z" /><path d="M9 12a3 3 0 1 0 6 0a3 3 0 0 0 -6 0" /></svg>
                  </div>
                  <div className="d-grid gap-3">
                    <button className="btn btn-primary text-white" onClick={handleCreateLeads}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-plus"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M12 5l0 14" /><path d="M5 12l14 0" /></svg>
                      Create Leads
                    </button>
                    <button className="btn btn-light text-muted" onClick={handleViewTask}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-checklist"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M9.615 20h-2.615a2 2 0 0 1 -2 -2v-12a2 2 0 0 1 2 -2h8a2 2 0 0 1 2 2v8" /><path d="M14 19l2 2l4 -4" /><path d="M9 8h4" /><path d="M9 12h2" /></svg>
                      View Task
                    </button>
                    {/* <button className="btn btn-light text-muted">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-calendar-time"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M11.795 21h-6.795a2 2 0 0 1 -2 -2v-12a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v4" /><path d="M18 18m-4 0a4 4 0 1 0 8 0a4 4 0 1 0 -8 0" /><path d="M15 3v4" /><path d="M7 3v4" /><path d="M3 11h16" /><path d="M18 16.496v1.504l1 1" /></svg>
                      View Audit Schedule
                    </button> */}
                  </div>
                </div>
              </div>
            </div>
          </div>
          

          {/* New 5 Cards Row */}
          <div className="row g-1 mt-3">
            <div className="col-md-2">
              <div className={`card h-100${hasActiveFilters ? ' stat-card-filtered' : ''}`}>
              <div className="card-status-start bg-primary"></div>
                <div className="card-body">
                  <div className="d-flex align-items-center gap-1">
                    <div className="subheader">Latest Follow Up (Leads)</div>
                    <InfoTooltip text="Number of leads followed up on recently." />
                  </div>
                  <div className="mt-3">
                    <div 
                      className="h1"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleNavigateToLeadsReportsWithTaskUpdate()}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        const params = buildLeadsReportsWithTaskUpdateParams();
                        const queryString = params.toString();
                        window.open(`/reports/leads${queryString ? `?${queryString}` : ''}`, '_blank');
                      }}
                      title="Click to view Leads Report filtered by Task Update Date (Right-click to open in new tab)"
                    >
                      <StatNum
                          loading={dashboardLoading}
                          filtered={dashboardData.statistics.latestFollowUpLeads ?? 0}
                          total={totalCounts.latestFollowUpLeads ?? 0}
                          isFiltered={hasActiveFilters}
                          light={false}
                        />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-2">
              <div className={`card h-100${hasActiveFilters ? ' stat-card-filtered' : ''}`}>
                <div className="card-status-start bg-primary"></div>
                <div className="card-body">
                  <div className="d-flex align-items-center gap-1">
                    <div className="subheader">Latest Updated (Leads)</div>
                    <InfoTooltip text="Number of leads with recent activity updates." />
                  </div>
                  <div className="mt-3">
                    <div 
                      className="h1"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleNavigateToLeadsReportsWithLeadUpdate()}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        const params = buildLeadsReportsWithLeadUpdateParams();
                        const queryString = params.toString();
                        window.open(`/reports/leads${queryString ? `?${queryString}` : ''}`, '_blank');
                      }}
                      title="Click to view Leads Report filtered by Assigned To and Lead Update Date (Right-click to open in new tab)"
                    >
                      <StatNum
                          loading={dashboardLoading}
                          filtered={dashboardData.statistics.latestUpdatedLeads ?? 0}
                          total={totalCounts.latestUpdatedLeads ?? 0}
                          isFiltered={hasActiveFilters}
                          light={false}
                        />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-2">
              <div className={`card h-100${hasActiveFilters ? ' stat-card-filtered' : ''}`}>
                <div className="card-status-start bg-primary"></div>
                <div className="card-body">
                  <div className="d-flex align-items-center gap-1">
                    <div className="subheader">Latest Updated (Proposal)</div>
                    <InfoTooltip text="Number of proposals with recent status changes." />
                  </div>
                  <div className="mt-3">
                    <div
                      className="h1"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleNavigateToProposalsReportsWithUpdate()}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        const params = buildProposalsReportsWithUpdateParams();
                        const queryString = params.toString();
                        window.open(`/reports/proposals${queryString ? `?${queryString}` : ''}`, '_blank');
                      }}
                      title="Click to view Proposals Report filtered by Update Date (Right-click to open in new tab)"
                    >
                      <StatNum
                          loading={dashboardLoading}
                          filtered={dashboardData.statistics.latestUpdatedProposal ?? 0}
                          total={totalCounts.latestUpdatedProposal ?? 0}
                          isFiltered={hasActiveFilters}
                          light={false}
                        />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-2">
              <div className={`card h-100${hasActiveFilters ? ' stat-card-filtered' : ''}`}>
                <div className="card-status-start bg-primary"></div>
                <div className="card-body">
                  <div className="d-flex align-items-center gap-1">
                    <div className="subheader">Proposal Not Converted</div>
                    <InfoTooltip text="Proposals that did not result in a project (Lost)." />
                  </div>
                  <div className="mt-3">
                    <div 
                      className="h1"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleNavigateToProposalsReportsNotConverted()}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        const params = buildProposalsReportsNotConvertedParams();
                        const queryString = params.toString();
                        window.open(`/reports/proposals${queryString ? `?${queryString}` : ''}`, '_blank');
                      }}
                      title="Click to view Proposals Report filtered by Not Converted status (Right-click to open in new tab)"
                    >
                      <StatNum
                          loading={dashboardLoading}
                          filtered={dashboardData.statistics.proposalNotConverted ?? 0}
                          total={totalCounts.proposalNotConverted ?? 0}
                          isFiltered={hasActiveFilters}
                          light={false}
                        />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-2">
              <div className={`card h-100${hasActiveFilters ? ' stat-card-filtered' : ''}`}>
                <div className="card-status-start bg-primary"></div>
                <div className="card-body">
                  <div className="d-flex align-items-center gap-1">
                    <div className="subheader">Converted to Project</div>
                    <InfoTooltip text="Proposals that successfully transitioned into active projects." />
                  </div>
                  <div className="mt-3">
                    <div 
                      className="h1"
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        const params = buildProjectsReportsConvertedParams();
                        const queryString = params.toString();
                        navigate(`/reports/projects${queryString ? `?${queryString}` : ''}`);
                      }}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        const params = buildProjectsReportsConvertedParams();
                        const queryString = params.toString();
                        window.open(`/reports/projects${queryString ? `?${queryString}` : ''}`, '_blank');
                      }}
                      title="Click to view Projects Report filtered by creation date (Right-click to open in new tab)"
                    >
                      <StatNum
                          loading={dashboardLoading}
                          filtered={dashboardData.statistics.convertedToProject ?? 0}
                          total={totalCounts.convertedToProject ?? 0}
                          isFiltered={hasActiveFilters}
                          light={false}
                        />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* New 6 Cards Row - Negotiation and Close Won Statuses */}
          <div className="row g-1 mt-3">
            <div className="col-md-12">
              <div className="h4">Proposal Stage</div>
            </div>
            <div className="col-md-2">
              <div className={`card h-100${hasActiveFilters ? ' stat-card-filtered' : ''}`}>
                <div className="card-status-start bg-primary"></div>
                <div className="card-body">
                  <div className="d-flex align-items-center gap-1">
                    <div className="subheader">Negotiation</div>
                    <InfoTooltip text="Proposals currently in the negotiation phase." />
                  </div>
                  <div className="mt-3">
                    <div 
                      className="h1"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleNavigateToProposalsReportsWithStage(1)}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        const params = buildProposalsReportsWithStageParams(1);
                        const queryString = params.toString();
                        window.open(`/reports/proposals${queryString ? `?${queryString}` : ''}`, '_blank');
                      }}
                      title="Click to view Proposals Report filtered by Negotiation stage (Right-click to open in new tab)"
                    >
                      <StatNum
                          loading={dashboardLoading}
                          filtered={dashboardData.statistics.negotiation ?? 0}
                          total={totalCounts.negotiation ?? 0}
                          isFiltered={hasActiveFilters}
                          light={false}
                        />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-2">
              <div className={`card h-100${hasActiveFilters ? ' stat-card-filtered' : ''}`}>
                <div className="card-status-start bg-primary"></div>
                <div className="card-body">
                  <div className="d-flex align-items-center gap-1">
                    <div className="subheader">Close M1</div>
                    <InfoTooltip text="Forecasted closings for the next month (M1)." />
                  </div>
                  <div className="mt-3">
                    <div 
                      className="h1"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleNavigateToProposalsReportsWithStage(2)}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        const params = buildProposalsReportsWithStageParams(2);
                        const queryString = params.toString();
                        window.open(`/reports/proposals${queryString ? `?${queryString}` : ''}`, '_blank');
                      }}
                      title="Click to view Proposals Report filtered by Close M1 stage (Right-click to open in new tab)"
                    >
                      <StatNum
                          loading={dashboardLoading}
                          filtered={dashboardData.statistics.closeWon1 ?? 0}
                          total={totalCounts.closeWon1 ?? 0}
                          isFiltered={hasActiveFilters}
                          light={false}
                        />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-2">
              <div className={`card h-100${hasActiveFilters ? ' stat-card-filtered' : ''}`}>
                <div className="card-status-start bg-blue"></div>
                <div className="card-body">
                  <div className="d-flex align-items-center gap-1">
                    <div className="subheader">Close M2</div>
                    <InfoTooltip text="Forecasted closings for the following month (M2)." />
                  </div>
                  <div className="mt-3">
                    <div 
                      className="h1"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleNavigateToProposalsReportsWithStage(3)}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        const params = buildProposalsReportsWithStageParams(3);
                        const queryString = params.toString();
                        window.open(`/reports/proposals${queryString ? `?${queryString}` : ''}`, '_blank');
                      }}
                      title="Click to view Proposals Report filtered by Close M2 stage (Right-click to open in new tab)"
                    >
                      <StatNum
                          loading={dashboardLoading}
                          filtered={dashboardData.statistics.closeWon2 ?? 0}
                          total={totalCounts.closeWon2 ?? 0}
                          isFiltered={hasActiveFilters}
                          light={false}
                        />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-2">
              <div className={`card h-100${hasActiveFilters ? ' stat-card-filtered' : ''}`}>
                <div className="card-status-start bg-blue"></div>
                <div className="card-body">
                  <div className="d-flex align-items-center gap-1">
                    <div className="subheader">Close M3</div>
                    <InfoTooltip text="Forecasted closings for the month after that (M3)." />
                  </div>
                  <div className="mt-3">
                    <div 
                      className="h1"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleNavigateToProposalsReportsWithStage(4)}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        const params = buildProposalsReportsWithStageParams(4);
                        const queryString = params.toString();
                        window.open(`/reports/proposals${queryString ? `?${queryString}` : ''}`, '_blank');
                      }}
                      title="Click to view Proposals Report filtered by Close M3 stage (Right-click to open in new tab)"
                    >
                      <StatNum
                          loading={dashboardLoading}
                          filtered={dashboardData.statistics.closeWon3 ?? 0}
                          total={totalCounts.closeWon3 ?? 0}
                          isFiltered={hasActiveFilters}
                          light={false}
                        />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-2">
              <div className={`card h-100${hasActiveFilters ? ' stat-card-filtered' : ''}`}>
                <div className="card-status-start bg-green"></div>
                <div className="card-body">
                  <div className="d-flex align-items-center gap-1">
                    <div className="subheader">Won</div>
                    <InfoTooltip text="Proposals that have been successfully won." />
                  </div>
                  <div className="mt-3">
                    <div 
                      className="h1"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleNavigateToProposalsReportsWithStage(5)}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        const params = buildProposalsReportsWithStageParams(5);
                        const queryString = params.toString();
                        window.open(`/reports/proposals${queryString ? `?${queryString}` : ''}`, '_blank');
                      }}
                      title="Click to view Proposals Report filtered by Won stage (Right-click to open in new tab)"
                    >
                      <StatNum
                          loading={dashboardLoading}
                          filtered={dashboardData.statistics.won ?? 0}
                          total={totalCounts.won ?? 0}
                          isFiltered={hasActiveFilters}
                          light={false}
                        />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-2">
              <div className={`card h-100${hasActiveFilters ? ' stat-card-filtered' : ''}`}>
                <div className="card-status-start bg-red"></div>
                <div className="card-body">
                  <div className="d-flex align-items-center gap-1">
                    <div className="subheader">Lost</div>
                    <InfoTooltip text="Proposals that have been lost to competitors or other reasons." />
                  </div>
                  <div className="mt-3">
                    <div 
                      className="h1"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleNavigateToProposalsReportsWithStage(6)}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        const params = buildProposalsReportsWithStageParams(6);
                        const queryString = params.toString();
                        window.open(`/reports/proposals${queryString ? `?${queryString}` : ''}`, '_blank');
                      }}
                      title="Click to view Proposals Report filtered by Lost stage (Right-click to open in new tab)"
                    >
                      <StatNum
                          loading={dashboardLoading}
                          filtered={dashboardData.statistics.lost ?? 0}
                          total={totalCounts.lost ?? 0}
                          isFiltered={hasActiveFilters}
                          light={false}
                        />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* New 6 Cards Row - Approval Workflow Statuses */}
          <div className="row g-1 mt-3">
            <div className="col-md-12">
              <div className="h4">Proposal by Status</div>
            </div>
            <div className="col-md-2">
              <div className={`card h-100${hasActiveFilters ? ' stat-card-filtered' : ''}`}>
                <div className="card-status-start bg-yellow"></div>
                <div className="card-body">
                  <div className="d-flex align-items-center gap-1">
                    <div className="subheader">Waiting for Approval</div>
                    <InfoTooltip text="Proposals awaiting internal approval." />
                  </div>
                  <div className="mt-3">
                    <div 
                      className="h1"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleNavigateToProposalsReportsWithStatus('approval', DASHBOARD_PROPOSAL_APPROVAL_STATUS_ID.NEED_APPROVAL)}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        const params = buildProposalsReportsWithStatusParams('approval', DASHBOARD_PROPOSAL_APPROVAL_STATUS_ID.NEED_APPROVAL);
                        const queryString = params.toString();
                        window.open(`/reports/proposals${queryString ? `?${queryString}` : ''}`, '_blank');
                      }}
                      title="Click to view Proposals Report filtered by Waiting for Approval status (Right-click to open in new tab)"
                    >
                      <StatNum
                          loading={dashboardLoading}
                          filtered={dashboardData.statistics.waitingForApproval ?? 0}
                          total={totalCounts.waitingForApproval ?? 0}
                          isFiltered={hasActiveFilters}
                          light={false}
                        />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-2">
              <div className={`card h-100${hasActiveFilters ? ' stat-card-filtered' : ''}`}>
                <div className="card-status-start bg-green"></div>
                <div className="card-body">
                  <div className="d-flex align-items-center gap-1">
                    <div className="subheader">Approval - Approved</div>
                    <InfoTooltip text="Proposals that have been internally approved." />
                  </div>
                  <div className="mt-3">
                    <div 
                      className="h1"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleNavigateToProposalsReportsWithStatus('approval', DASHBOARD_PROPOSAL_APPROVAL_STATUS_ID.APPROVED)}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        const params = buildProposalsReportsWithStatusParams('approval', DASHBOARD_PROPOSAL_APPROVAL_STATUS_ID.APPROVED);
                        const queryString = params.toString();
                        window.open(`/reports/proposals${queryString ? `?${queryString}` : ''}`, '_blank');
                      }}
                      title="Click to view Proposals Report filtered by Approved status (Right-click to open in new tab)"
                    >
                      <StatNum
                          loading={dashboardLoading}
                          filtered={dashboardData.statistics.approvalApproved ?? 0}
                          total={totalCounts.approvalApproved ?? 0}
                          isFiltered={hasActiveFilters}
                          light={false}
                        />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-2">
              <div className={`card h-100${hasActiveFilters ? ' stat-card-filtered' : ''}`}>
                <div className="card-status-start bg-primary"></div>
                <div className="card-body">
                  <div className="d-flex align-items-center gap-1">
                    <div className="subheader">Send to Client</div>
                    <InfoTooltip text="Proposals that have been sent to the client." />
                  </div>
                  <div className="mt-3">
                    <div 
                      className="h1"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleNavigateToProposalsReportsWithStatus('proposal', DASHBOARD_PROPOSAL_PROPOSAL_STATUS_ID.SEND_TO_CLIENT)}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        const params = buildProposalsReportsWithStatusParams('proposal', DASHBOARD_PROPOSAL_PROPOSAL_STATUS_ID.SEND_TO_CLIENT);
                        const queryString = params.toString();
                        window.open(`/reports/proposals${queryString ? `?${queryString}` : ''}`, '_blank');
                      }}
                      title="Click to view Proposals Report filtered by Send to Client status (Right-click to open in new tab)"
                    >
                      <StatNum
                          loading={dashboardLoading}
                          filtered={dashboardData.statistics.sendToClient ?? 0}
                          total={totalCounts.sendToClient ?? 0}
                          isFiltered={hasActiveFilters}
                          light={false}
                        />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-2">
              <div className={`card h-100${hasActiveFilters ? ' stat-card-filtered' : ''}`}>
                <div className="card-status-start bg-yellow"></div>
                <div className="card-body">
                  <div className="d-flex align-items-center gap-1">
                    <div className="subheader">Need Revision</div>
                    <InfoTooltip text="Proposals that require internal or client revision." />
                  </div>
                  <div className="mt-3">
                    <div 
                      className="h1"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleNavigateToProposalsReportsWithStatus('proposal', DASHBOARD_PROPOSAL_PROPOSAL_STATUS_ID.FEEDBACK)}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        const params = buildProposalsReportsWithStatusParams('proposal', DASHBOARD_PROPOSAL_PROPOSAL_STATUS_ID.FEEDBACK);
                        const queryString = params.toString();
                        window.open(`/reports/proposals${queryString ? `?${queryString}` : ''}`, '_blank');
                      }}
                      title="Click to view Proposals Report filtered by Feedback status (Right-click to open in new tab)"
                    >
                      <StatNum
                          loading={dashboardLoading}
                          filtered={dashboardData.statistics.needRevision ?? 0}
                          total={totalCounts.needRevision ?? 0}
                          isFiltered={hasActiveFilters}
                          light={false}
                        />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-2">
              <div className={`card h-100${hasActiveFilters ? ' stat-card-filtered' : ''}`}>
                <div className="card-status-start bg-red"></div>
                <div className="card-body">
                  <div className="d-flex align-items-center gap-1">
                    <div className="subheader">Decline</div>
                    <InfoTooltip text="Proposals that have been declined." />
                  </div>
                  <div className="mt-3">
                    <div 
                      className="h1"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleNavigateToProposalsReportsWithStatus('proposal', DASHBOARD_PROPOSAL_PROPOSAL_STATUS_ID.REJECTED)}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        const params = buildProposalsReportsWithStatusParams('proposal', DASHBOARD_PROPOSAL_PROPOSAL_STATUS_ID.REJECTED);
                        const queryString = params.toString();
                        window.open(`/reports/proposals${queryString ? `?${queryString}` : ''}`, '_blank');
                      }}
                      title="Click to view Proposals Report filtered by Decline status (Right-click to open in new tab)"
                    >
                      <StatNum
                          loading={dashboardLoading}
                          filtered={dashboardData.statistics.decline ?? 0}
                          total={totalCounts.decline ?? 0}
                          isFiltered={hasActiveFilters}
                          light={false}
                        />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-2">
              <div className={`card h-100${hasActiveFilters ? ' stat-card-filtered' : ''}`}>
                <div className="card-status-start bg-green"></div>
                <div className="card-body">
                  <div className="d-flex align-items-center gap-1">
                    <div className="subheader">Accepted</div>
                    <InfoTooltip text="Proposals that have been accepted by the client." />
                  </div>
                  <div className="mt-3">
                    <div 
                      className="h1"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleNavigateToProposalsReportsWithStatus('proposal', DASHBOARD_PROPOSAL_PROPOSAL_STATUS_ID.ACCEPTED)}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        const params = buildProposalsReportsWithStatusParams('proposal', DASHBOARD_PROPOSAL_PROPOSAL_STATUS_ID.ACCEPTED);
                        const queryString = params.toString();
                        window.open(`/reports/proposals${queryString ? `?${queryString}` : ''}`, '_blank');
                      }}
                      title="Click to view Proposals Report filtered by Accepted status (Right-click to open in new tab)"
                    >
                      <StatNum
                          loading={dashboardLoading}
                          filtered={dashboardData.statistics.accepted ?? 0}
                          total={totalCounts.accepted ?? 0}
                          isFiltered={hasActiveFilters}
                          light={false}
                        />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-12 mt-3">
            <div className='card h-100'>
              <div className='card-body'>
                <div className='row'>
                  <div className="col-md-10">
                    <div className="h3 mb-0">Companies Contacted per Team Member</div>
                    <div className="h4 text-muted mb-0">Shows the amount of outreach performed.</div>
                  </div>
                  <div className="d-flex align-items-center justify-content-end col-md-2">
                    <EllipsisVertical className="icon icon-tabler text-muted " />
                  </div>
                  <div className='col-md-12'>
                    <ColumnChart
                      data={dashboardData.teamMemberData.data}
                      categories={dashboardData.teamMemberData.categories}
                      yAxisTitle="Companies Contacted"
                      onCategoryClick={handleNavigateToTaskWithTeamMember}
                      onCategoryRightClick={(teamMemberName, categoryIndex) => {
                        // Build URL with filters for right-click (open in new tab)
                        const params = new URLSearchParams();
                        
                        // Get user ID from teamMemberData if available
                        let userId = null;
                        if (dashboardData.teamMemberData.userIds && Array.isArray(dashboardData.teamMemberData.userIds)) {
                          const index = typeof categoryIndex === 'number' ? categoryIndex : parseInt(categoryIndex, 10);
                          if (!isNaN(index) && index >= 0 && index < dashboardData.teamMemberData.userIds.length) {
                            userId = dashboardData.teamMemberData.userIds[index];
                          }
                        }
                        
                        // Fallback: If userIds array is not available, try to find user ID from salesUsersData by name
                        if (!userId && teamMemberName) {
                          const user = salesUsersData.find(u => {
                            const fullName = u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim();
                            return fullName === teamMemberName;
                          });
                          if (user && user.id) {
                            userId = user.id;
                          }
                        }
                        
                        // Add assigned user filter if we have the user ID
                        if (userId) {
                          params.append('assignedUser', userId.toString());
                        }
                        
                        // Add status filter to show only 'in progress' and 'done' tasks
                        params.append('status', 'in progress,done');
                        
                        // Add date range filter based on dashboard period
                        if (filters.period && filters.period !== 'all') {
                          if (filters.period === 'custom' && customDateRange.startDate && customDateRange.endDate) {
                            params.append('dateFrom', customDateRange.startDate);
                            params.append('dateTo', customDateRange.endDate);
                          } else {
                            const { startDate: dateFrom, endDate: dateTo } = getPeriodDateRange(filters.period);
                            if (dateFrom) params.append('dateFrom', dateFrom);
                            if (dateTo) params.append('dateTo', dateTo);
                          }
                        }
                        
                        // ✅ Add sector, source, and standard filters from dashboard
                        if (serializedFilters.sector !== 'all') {
                          params.append('sector', serializedFilters.sector);
                        }
                        if (serializedFilters.source !== 'all') {
                          params.append('source', serializedFilters.source);
                        }
                        if (serializedFilters.standard !== 'all') {
                          params.append('standard', serializedFilters.standard);
                        }
                        if (serializedFilters.service !== 'all') {
                          params.append('service', serializedFilters.service);
                        }

                        const queryString = params.toString();
                        window.open(`/task${queryString ? `?${queryString}` : ''}`, '_blank');
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-12 mt-3">
            <div className='row'>
              <div className='col-md-6'>
                <div className='card h-100'>
                  <div className='card-body'>
                    <div className='row flex-grow-1'>
                      <div className="col-md-10">
                        <div className="h3 mb-0">Sales Funnel</div>
                        <div className="h4 text-muted mb-0">From Prospect to Contract.</div>
                      </div>
                      <div className="d-flex align-items-center justify-content-end col-md-2">
                        <EllipsisVertical className="icon icon-tabler text-muted " />
                      </div>
                      <div className='col-md-12 justify-content-center mt-3'>
                        {dashboardLoading ? (
                          <div className="py-3">
                            {[80, 65, 50, 35, 20].map((width, i) => (
                              <div key={i} className="d-flex justify-content-center mb-2">
                                <div
                                  className="skeleton"
                                  style={{
                                    width: `${width}%`,
                                    height: '36px',
                                    borderRadius: '4px',
                                    background: 'linear-gradient(90deg, #e8e8e8 25%, #f5f5f5 50%, #e8e8e8 75%)',
                                    backgroundSize: '200% 100%',
                                    animation: 'shimmer 1.4s infinite',
                                  }}
                                />
                              </div>
                            ))}
                            <style>{`@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }`}</style>
                          </div>
                        ) : (
                        <FunnelChart
                          key={`funnel-${filters.period}-${serializedFilters.source}-${serializedFilters.sector}-${serializedFilters.businessUnits}-${serializedFilters.people}`}
                          data={dashboardData.salesFunnelData.data}
                          categories={dashboardData.salesFunnelData.categories}
                          proposalValue={dashboardData.salesFunnelData.proposalValue}
                          formattedProposalValue={dashboardData.salesFunnelData.formattedProposalValue}
                          negotiationValue={dashboardData.salesFunnelData.negotiationValue}
                          contractWonValue={dashboardData.salesFunnelData.contractWonValue}
                          projectsValue={dashboardData.salesFunnelData.projectsValue}
                          onCategoryClick={handleSalesFunnelClick}
                          onCategoryRightClick={(category, categoryIndex) => {
                            // Right-click opens in new tab with same navigation logic as left-click.
                            // Reuse the named builder functions so logic stays in one place.
                            switch (category) {
                              case 'Leads':
                                const leadsParams = buildLeadsReportsParams();
                                window.open(`/reports/leads${leadsParams.toString() ? `?${leadsParams.toString()}` : ''}`, '_blank');
                                break;
                              case 'Proposal':
                                // Sales Funnel Proposal uses createdAt to match backend p.created_at basis.
                                const proposalsParams = buildProposalsReportsWithCreatedAtParams();
                                window.open(`/reports/proposals${proposalsParams.toString() ? `?${proposalsParams.toString()}` : ''}`, '_blank');
                                break;
                              case 'Negotiation':
                                // ✅ FIX: Use stageUpdatePeriod instead of proposalDate to match backend which now uses stage_updated_at
                                const negotiationParams = buildProposalsReportsWithStageParams(1);
                                window.open(`/reports/proposals${negotiationParams.toString() ? `?${negotiationParams.toString()}` : ''}`, '_blank');
                                break;
                              case 'Contract/Won':
                                // ✅ FIX: Use stageUpdatePeriod instead of proposalDate to match backend which now uses stage_updated_at
                                const contractWonParams = buildProposalsReportsWithStageParams(5);
                                window.open(`/reports/proposals${contractWonParams.toString() ? `?${contractWonParams.toString()}` : ''}`, '_blank');
                                break;
                              case 'Project':
                                // Project segment filters by Audit Date (propose_date)
                                const projectsParams = buildProjectsReportsByAuditDateParams();
                                window.open(`/reports/projects${projectsParams.toString() ? `?${projectsParams.toString()}` : ''}`, '_blank');
                                break;
                              default:
                                break;
                            }
                          }}
                        />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className='col-md-6'>
                <div className='card h-100'>
                  <div className='card-body'>
                    <div className='row'>
                      <div className="col-md-10">
                        <div className="h3 mb-0">Proposal Breakdown</div>
                        <div className="h4 text-muted mb-0">Analysis by Status</div>
                      </div>
                      <div className="d-flex align-items-center justify-content-end col-md-2">
                        <EllipsisVertical className="icon icon-tabler text-muted " />
                      </div>
                      <div className="col-md-12 mt-3">
                        <DonutChart
                          series={dashboardData.proposalBreakdownData.series}
                          labels={dashboardData.proposalBreakdownData.labels}
                          colors={dashboardData.proposalBreakdownData.colors}
                          width="100%"
                          height="350"
                          type="type_distinct"
                          onDataPointClick={handleProposalBreakdownClick}
                          onDataPointRightClick={(label, index, value) => {
                            const statusId = proposalBreakdownLabelToProposalStatusId(label);
                            const params = buildProposalsReportsWithStatusParams('proposal', statusId, true);
                            const queryString = params.toString();
                            window.open(`/reports/proposals${queryString ? `?${queryString}` : ''}`, '_blank');
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-12 mt-3">
            <div className='row'>
              <div className='col-md-6 d-flex'>
                <div className='card h-100 w-100'>
                  <div className='card-body'>
                    <div className='row flex-grow-1'>
                      <div className="col-md-10">
                        <div className="h3 mb-0">Leads by Source</div>
                        <div className="h4 text-muted mb-0">Identifies the most effective acquisition channels.</div>
                      </div>
                      <div className="d-flex align-items-center justify-content-end col-md-2">
                        <EllipsisVertical className="icon icon-tabler text-muted " />
                      </div>
                      <div className='col-md-12 mt-3'>
                        <DonutChart
                          series={dashboardData.leadsBySourceData.series}
                          labels={dashboardData.leadsBySourceData.labels}
                          width="100%"
                          height="350"
                          legendPosition="bottom"
                          type="type_cbqa"
                          onDataPointClick={handleLeadsBySourceClick}
                          onDataPointRightClick={(label, index, value) => {
                            // Find the source ID from sourcesData by matching the source name
                            const source = sourcesData.find(s => s.name === label);
                            const sourceId = source ? source.id : 'all';
                            
                            // Build params for Leads Reports with source filter
                            const params = new URLSearchParams();

                            if (serializedFilters.people !== 'all') {
                              params.append('people', serializedFilters.people);
                            }
                            if (serializedFilters.businessUnits !== 'all') {
                              params.append('businessUnits', serializedFilters.businessUnits);
                            }
                            if (serializedFilters.sector !== 'all') {
                              params.append('sector', serializedFilters.sector);
                            }
                            if (sourceId && sourceId !== 'all') {
                              params.append('source', sourceId);
                            }
                            if (serializedFilters.standard !== 'all') {
                              params.append('standard', serializedFilters.standard);
                            }
                            if (serializedFilters.service !== 'all') {
                              params.append('service', serializedFilters.service);
                            }
                            if (filters.period && filters.period !== 'all') {
                              params.append('period', filters.period);
                            }
                            
                            if (filters.period === 'custom' && customDateRange.startDate && customDateRange.endDate) {
                              params.append('customStartDate', customDateRange.startDate);
                              params.append('customEndDate', customDateRange.endDate);
                            }
                            
                            const queryString = params.toString();
                            window.open(`/reports/leads${queryString ? `?${queryString}` : ''}`, '_blank');
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className='col-md-6'>
                <div className='card h-100'>
                  <div className='card-body'>
                    <div className='row'>
                      <div className="col-md-10">
                        <div className="h3 mb-0">Task Management</div>
                        <div className="h4 text-muted mb-0">Shows active tasks (To Do & In Progress) by type and their current status.</div>
                      </div>
                      <div className="d-flex align-items-center justify-content-end col-md-2">
                        <EllipsisVertical className="icon icon-tabler text-muted " />
                      </div>
                      {/* ✅ FIX: Add scroll container with max 5 items visible */}
                      <div 
                        className="w-full max-w-md mx-auto p-4"
                        style={{
                          maxHeight: '400px',
                          overflowY: 'auto',
                          overflowX: 'hidden'
                        }}
                      >
                        <div className="d-flex flex-column gap-3">
                          {dashboardData.taskManagementData.map((task, index) => (
                            <div
                              key={index}
                              className={`p-3 rounded row`}
                              style={{ 
                                backgroundColor: task.bgColor,
                                cursor: 'pointer',
                                transition: 'opacity 0.2s'
                              }}
                              onClick={() => handleNavigateToTask(task)}
                              onContextMenu={(e) => {
                                e.preventDefault();
                                const params = new URLSearchParams();
                                
                                // ✅ FIX: Filter by task type using title case format
                                if (task.title) {
                                  const titleToTypeMap = {
                                    'Follow Up Calls': 'Follow Up',
                                    'Follow Up Email': 'Follow Up Email',
                                    'Follow Up Meeting': 'Follow Up Meeting',
                                    'Follow Up Call': 'Follow Up Call',
                                    'Proposal Documentation': 'Proposal',
                                    'Contract Review': 'Contract',
                                    'Client Meetings': 'Meeting',
                                    'Reminder': 'Reminder',
                                    'Email': 'Email',
                                    'Call': 'Call',
                                    'Telegram': 'Telegram',
                                    'Other Tasks': 'Other'
                                  };
                                  const taskType = titleToTypeMap[task.title] || task.title;
                                  params.append('type', taskType);
                                }

                                // Note: Task management items represent task TYPES, not statuses.
                                // Do not filter by status when right-clicking task management items.
                                
                                // Add due date range filter based on dashboard period
                                if (filters.period && filters.period !== 'all') {
                                  if (filters.period === 'custom' && customDateRange.startDate && customDateRange.endDate) {
                                    params.append('dueDateFrom', customDateRange.startDate);
                                    params.append('dueDateTo', customDateRange.endDate);
                                  } else {
                                    const { startDate: dateFrom, endDate: dateTo } = getPeriodDateRange(filters.period);
                                    if (dateFrom) params.append('dueDateFrom', dateFrom);
                                    if (dateTo) params.append('dueDateTo', dateTo);
                                  }
                                }
                                
                                // ✅ Add sector, source, and standard filters from dashboard
                                if (serializedFilters.sector !== 'all') {
                                  params.append('sector', serializedFilters.sector);
                                }
                                if (serializedFilters.source !== 'all') {
                                  params.append('source', serializedFilters.source);
                                }
                                if (serializedFilters.standard !== 'all') {
                                  params.append('standard', serializedFilters.standard);
                                }
                                if (serializedFilters.service !== 'all') {
                                  params.append('service', serializedFilters.service);
                                }
                                
                                const queryString = params.toString();
                                window.open(`/task${queryString ? `?${queryString}` : ''}`, '_blank');
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.opacity = '0.8';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.opacity = '1';
                              }}
                              title="Click to view Task page filtered by status (Right-click to open in new tab)"
                            >
                              <div className="d-flex align-items-center space-x-3 col-md-6">
                                <span
                                  className={`w-3 h-3 rounded-circle`}
                                  style={{ backgroundColor: task.dotColor }}
                                ></span>
                                <div className="font-medium text-sm">{task.title}</div>
                              </div>
                              <div className='col-md-6 text-end'>
                                <div className="d-flex gap-1 justify-content-end">
                                  {task.toDoCount > 0 && (
                                    <span
                                      className="badge"
                                      style={{
                                        backgroundColor: "#FFE6E6",
                                        color: "#B91C1C",
                                        fontSize: "11px",
                                        padding: "2px 6px",
                                        cursor: "pointer"
                                      }}
                                      title={`Click to view ${task.toDoCount} task${task.toDoCount === 1 ? '' : 's'} to do`}
                                      onClick={(e) => {
                                        e.stopPropagation(); // Prevent parent click
                                        handleNavigateToTaskWithStatus(task, 'to do');
                                      }}
                                    >
                                      {task.toDoCount} To Do
                                    </span>
                                  )}
                                  {task.inProgressCount > 0 && (
                                    <span
                                      className="badge"
                                      style={{
                                        backgroundColor: "#FFF8E6",
                                        color: "#BF7C00",
                                        fontSize: "11px",
                                        padding: "2px 6px",
                                        cursor: "pointer"
                                      }}
                                      title={`Click to view ${task.inProgressCount} task${task.inProgressCount === 1 ? '' : 's'} in progress`}
                                      onClick={(e) => {
                                        e.stopPropagation(); // Prevent parent click
                                        handleNavigateToTaskWithStatus(task, 'in progress');
                                      }}
                                    >
                                      {task.inProgressCount} In Progress
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                          {(!dashboardData.taskManagementData || dashboardData.taskManagementData.length === 0) && (
                            <div className="text-center py-4 text-muted">
                              <div className="mb-2">
                                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-clipboard-x">
                                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                  <path d="M9 5h-2a2 2 0 0 0 -2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-10a2 2 0 0 0 -2 -2h-2" />
                                  <path d="M9 3m0 2a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v0a2 2 0 0 1 -2 2h-2a2 2 0 0 1 -2 -2z" />
                                  <path d="M9 14l6 6" />
                                  <path d="M15 14l-6 6" />
                                </svg>
                              </div>
                              <p className="mb-0">No To Do or In Progress tasks available</p>
                              <small className="text-muted">All tasks are completed or no active tasks found</small>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-12 mt-3">
                <div className='card h-100'>
                  <div className='card-body'>
                    <div className='row'>
                      <div className="col-md-10">
                        <div className="h3 mb-0">Leads by Industry Sector</div>
                        <div className="h4 text-muted mb-0">Distribution of clients across various industries.</div>
                      </div>
                      <div className="d-flex align-items-center justify-content-end col-md-2">
                        <EllipsisVertical className="icon icon-tabler text-muted " />
                      </div>
                      <div className='col-md-12'>
                        <ColumnChart
                          data={dashboardData.leadsByIndustryData.data}
                          categories={dashboardData.leadsByIndustryData.categories}
                          yAxisTitle="Number of Leads"
                          singleColor="#39C0C3"
                          variant="wrapped"
                          onCategoryClick={handleLeadsByIndustryClick}
                          onCategoryRightClick={(label, index) => {
                            // Find the sector ID from sectorsData by matching the sector name
                            const sector = sectorsData.find(s => s.name === label);
                            const sectorId = sector ? sector.id : 'all';
                            
                            // Build params for Leads Reports with sector filter
                            const params = new URLSearchParams();
                            
                            if (serializedFilters.people !== 'all') {
                              params.append('people', serializedFilters.people);
                            }
                            if (serializedFilters.businessUnits !== 'all') {
                              params.append('businessUnits', serializedFilters.businessUnits);
                            }
                            if (serializedFilters.source !== 'all') {
                              params.append('source', serializedFilters.source);
                            }
                            if (sectorId && sectorId !== 'all') {
                              params.append('sector', sectorId);
                            }
                            if (serializedFilters.standard !== 'all') {
                              params.append('standard', serializedFilters.standard);
                            }
                            if (serializedFilters.service !== 'all') {
                              params.append('service', serializedFilters.service);
                            }
                            if (filters.period && filters.period !== 'all') {
                              params.append('period', filters.period);
                            }
                            
                            if (filters.period === 'custom' && customDateRange.startDate && customDateRange.endDate) {
                              params.append('customStartDate', customDateRange.startDate);
                              params.append('customEndDate', customDateRange.endDate);
                            }
                            
                            const queryString = params.toString();
                            window.open(`/reports/leads${queryString ? `?${queryString}` : ''}`, '_blank');
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
      
      {/* ✅ TAMBAHAN: Approval Detail Modal */}
      <Modal
        isOpen={showApprovalDetailModal}
        onClose={handleCloseApprovalDetailModal}
        title="Approval Request Details"
        size="xl"
      >
        <div className="modal-body">
          {loadingApprovalDetails ? (
            <div className="text-center py-4">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading approval details...</p>
            </div>
          ) : (
            <>
              {/* Approval Request Info */}
              {selectedApproval && (
                <div className="mb-4">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-2">
                        <strong>Proposal Number:</strong> {selectedApproval.proposalcode || '-'}
                      </div>
                      <div className="mb-2">
                        <strong>Client:</strong> {selectedApproval.client || '-'}
                      </div>
                      <div className="mb-2">
                        <strong>Initiator:</strong> {selectedApproval.initiator || '-'}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-2">
                        <strong>Approval Step:</strong> {selectedApproval.approval_step_name || selectedApproval.approvalStepName || `Step ${selectedApproval.approval_step || '-'}`}
                      </div>
                      <div className="mb-2">
                        <strong>Due Date:</strong> {selectedApproval.duedate || selectedApproval.dueDate || selectedApproval.due_date ? new Date(selectedApproval.duedate || selectedApproval.dueDate || selectedApproval.due_date).toLocaleDateString() : '-'}
                      </div>
                      <div className="mb-2">
                        <strong>Request Date:</strong> {selectedApproval.request_date || selectedApproval.requestDate ? new Date(selectedApproval.request_date || selectedApproval.requestDate).toLocaleDateString() : '-'}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Workflow Items Table */}
              <div className="mb-4">
                <h5 className="mb-3">Items Requiring Approval</h5>
                {approvalWorkflowItems.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-vcenter">
                      <thead>
                        <tr>
                          <th>Service</th>
                          <th>Standard</th>
                          <th>Product Category</th>
                          <th>Service Item</th>
                          <th>Qty</th>
                          <th>Unit</th>
                          <th>Unit Price</th>
                          <th>Workflow Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {approvalWorkflowItems.map((item, index) => (
                          <tr key={index}>
                            <td>{item.serviceName}</td>
                            <td>{item.standardName}</td>
                            <td>{item.productCategoryName}</td>
                            <td>{item.serviceItemName}</td>
                            <td>{item.qty}</td>
                            <td>{item.unit}</td>
                            <td>
                              {item.unitPrice ? 
                                item.unitPrice.toLocaleString('id-ID', {
                                  style: 'currency',
                                  currency: item.currencyCode || 'IDR'
                                }) : 
                                (item.currencyCode || 'IDR') === 'USD' ? '$0' : 'Rp 0'
                              }
                            </td>
                            <td>
                              <span className={`badge badge-${item.workflowStatus === 'pending' ? 'warning' : 'info'}`}>
                                {item.workflowStatus === 'pending' ? 'Pending Review' : 'Needs Approval'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-muted">No workflow items found for this approval request.</p>
                  </div>
                )}
              </div>

              {/* Note Section - Always visible */}
              <div className="mb-4">
                <label className="form-label fw-bold">Note from Initiator</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={approvalNote || selectedApproval?.note || ''}
                  placeholder="No note provided."
                  disabled
                  readOnly
                  style={{ backgroundColor: '#f8f9fa' }}
                />
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleCloseApprovalDetailModal}
          >
            Close
          </button>
        </div>
      </Modal>

      {/* Leads Form Modal */}
      <LeadsForm
        formData={formData}
        formErrors={formErrors}
        handleInputChange={handleInputChange}
        handleTextChange={handleTextChange}
        handleFormSubmit={handleFormSubmit}
        serviceSections={serviceSections}
        addServiceSection={addServiceSection}
        deleteServiceSection={deleteServiceSection}
        uploadedFiles={uploadedFiles}
        onDrop={onDrop}
        removeFile={removeFile}
        options={options}
        isOpen={isLeadsModalOpen}
        onClose={handleLeadsModalClose}
        fetchContactOptions={fetchContactOptions}
        standardsOptionsMap={standardsOptionsMap}
        onSuccessSubmit={handleLeadsSuccessSubmit}
      />
    </div>
  );
};

export default Dashboard;
